import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Platform,
  ScrollView,
  Text,
  TouchableOpacity,
  useWindowDimensions,
  View,
} from 'react-native';
import MaterialIcons from 'react-native-vector-icons/MaterialIcons';
import { useDispatch, useSelector } from 'react-redux';
import { DLOSimpleBreadcrumb } from '../../../../modules/dlo/components/DLOBreadcrumb';
import DLOModal from '../../../../modules/dlo/components/DLOModal';
import { RootState } from '../../../../store';
import { useToast } from '../../../../components/LhToastContainer';
import { fetchPeriodWeeks } from '../../../../api/periodWeeksApi';
import { getPreviousPeriodAndWeek } from '../../../../utils/filterOptions';
import { KPIFilterDropdownItem } from '../../../AnalyticsHub/components/KPIFilterDropdown';
import { useAdminLocations } from '../../../AnalyticsHub/hooks/useAdminLocations';
import { useDisabledPeriodWeekIds } from '../../../AnalyticsHub/hooks/useDisabledPeriodWeekIds';
import { getValidIconName } from '../../../AnalyticsHub/utils/reportsTabHelpers';
import formatToLocalWithTZ, { formatRecommendationWindow } from '../../constants/dateHelpers';
import OrderRulesFilterBar, { ExtendedFilterKey, QuickTimeFrameKey } from './components/OrderRulesFilterBar';
import ExportButton from './components/ExportButton';
import { useOrderRulesDerivedData } from './hooks/useOrderRulesDerivedData';
import styles from './OrderRulesReport.styles';
import CustomerSkuUtilizationTab, { QuickFilterDimension } from './tabs/CustomerSkuUtilizationTab';
import ExecutiveOverviewTab from './tabs/ExecutiveOverviewTab';
import MaxPotentialTab from './tabs/MaxPotentialTab';
import OrderRuleCheckTab from './tabs/OrderRuleCheckTab';
import RecommendationsTab from './tabs/RecommendationsTab';
import SalesDetailsTab from './tabs/SalesDetailsTab';
import {
  fetchOrderRulesSummary,
  fetchOrderRulesChartMaxPotential,
  fetchOrderRulesChartEnrollmentTrend,
  fetchOrderRulesChartLayerPerformance,
  fetchOrderRulesRecommendations,
  fetchOrderRulesCheck,
  fetchOrderRulesMaxPotentialExecution,
  fetchOrderRulesSalesVolume,
  fetchCustomerSkuUtilization,
  fetchOrderRulesFilterData,
  fetchOrderRulesFilterDataAutocomplete,
  fetchOrderRulesLastRefresh,
  exportOrderRulesReport,
  OrderRulesExportOptions,
  OrderRulesExportReportType,
  normalizeSummaryResponse,
  normalizeMaxPotentialChartResponse,
  normalizeEnrollmentTrendResponse,
  normalizeLayerPerformanceResponse,
  normalizeRecommendationsResponse,
  normalizeOrderRuleCheckResponse,
  normalizeMaxPotentialExecutionResponse,
  normalizeSalesVolumeResponse,
  normalizeCustomerSkuUtilizationResponse,
  OrderRulesDashboardResponse,
  OrderRulesFilterParams,
  OrderRulesLastRefreshData,
} from './orderRulesApi';
import {
  setOrderRulesFilterDataLoading,
  setOrderRulesFilterData,
  setOrderRulesFilterDataError,
  appendOrderRulesFilterCustomers,
  appendOrderRulesFilterMaterials,
} from '../../../../store/slices/orderRulesFilterDataSlice';

const REPORT_LINK = '/navigator-reports/order-rules';

// Maps the SKU Recommendations UI column keys to the exact backend response
// field names, since sortBy must match the API's field names, not the UI's.
const RECO_SORT_FIELD_MAP: Record<string, string> = {
  location: 'location',
  customer: 'customerName',
  materialNumber: 'materialNo',
  packageName: 'packageName',
  description: 'materialDescription',
  skuEligible: 'skuEligible',
  recommendation: 'recommend',
  confidence: 'confidence',
  customerEnrolled: 'customerEnrolled',
  casesPerLayer: 'casesPerLayer',
  pyPlus6AvgCases: 'pyPlus6AvgCasesPerWeek',
  pyMinus6AvgCases: 'pyMinus6AvgCasesPerWeek',
  cyMinus6AvgCases: 'cyMinus6AvgCasesPerWeek',
  pyPlus6GteLayer: 'pyPlus6AvgAtLeastLayer',
  pyMinus6GteLayer: 'pyMinus6AvgAtLeastLayer',
  cyMinus6GteLayer: 'cyMinus6AvgAtLeastLayer',
  region: 'region',
  market: 'market',
  customerBp: 'customerBp',
};

// Maps the Sales Details UI column keys to the OrderRulesSalesVolumeRecord field names.
const SALES_SORT_FIELD_MAP: Record<string, string> = {
  region: 'region',
  market: 'market',
  location: 'location',
  customerBp: 'customerBp',
  customer: 'customerName',
  materialNumber: 'materialNo',
  packageName: 'packageName',
  description: 'materialDescription',
  layer: 'casesPerLayer',
  avgCases: 'avgCasesPerWk',
};

// Maps the Customer & SKU Utilization UI column keys to the CustomerSkuUtilizationRecord field names.
const SKU_UTIL_SORT_FIELD_MAP: Record<string, string> = {
  region: 'region',
  market: 'market',
  location: 'location',
  customerBp: 'customerBp',
  customer: 'customer',
  packageName: 'packageName',
  materialNumber: 'materialNo',
  description: 'materialDescription',
  enrolledSkus: 'enrolledSkus',
  eligibleSkus: 'eligibleSkus',
  customerPerf: 'customerPerformancePct',
  enrolledCustomers: 'enrolledCustomers',
  totalLargeFormat: 'totalLargeFormat',
  skuPerf: 'skuPerformancePct',
  volumeEligibleSkus: 'lastPeriodSalesVolumeEligibleSkus',
  volumeAllSkus: 'lastPeriodSalesVolumeAllSkus',
};

// Maps the Max Potential Execution UI column keys to the MaxPotentialExecutionRow field names.
const MAX_POT_SORT_FIELD_MAP: Record<string, string> = {
  region: 'region',
  market: 'market',
  buildLocation: 'buildLocation',
  skuSlots: 'skuSlots',
  lastUpdatedAt: 'lastUpdateOrderRules',
  layerPerformancePct: 'layerPerformancePct',
  maxPlanPct: 'maxPlanPct',
  optimizedPlanPct: 'optimizedPlanPct',
  maxPotentialPct: 'maxPotentialPct',
  estimatedLaborSavings: 'estCostSavings',
};

const normalizeFilterValue = (value: unknown): string =>
  String(value ?? '').trim().replace(/\s+/g, ' ').toLowerCase();

type OrderRulesTab =
  | 'executive-overview'
  | 'recommendations'
  | 'sales-details'
  | 'customer-sku-utilization'
  | 'max-potential'
  | 'order-rule-check';

// Filters that don't apply to a given tab render read-only, matching the
// consolidated report's per-tab filter scoping.
const TAB_DISABLED_FILTERS: Record<OrderRulesTab, ExtendedFilterKey[]> = {
  'executive-overview': [],
  recommendations: ['year', 'period', 'week'],
  'sales-details': [],
  'customer-sku-utilization': ['year', 'period', 'week'],
  'max-potential': ['customerBp', 'customer', 'keyAccount', 'package', 'materialNumber', 'material'],
  'order-rule-check': ['customerBp', 'customer', 'keyAccount', 'year', 'period', 'week'],
};

// Tooltip shown next to a disabled filter's label, explaining why it doesn't apply on this tab.
const TAB_DISABLED_FILTER_TOOLTIP: Partial<Record<OrderRulesTab, string>> = {
  recommendations: 'Timeframe filters do not apply, showing data between the recommendation window dates.',
  'customer-sku-utilization': 'Timeframe filters do not apply, showing current enrollment and the last closed period sales volume.',
  'order-rule-check': 'Customer and Timeframe filters do not apply to this table.',
};

const OrderRulesReport: React.FC = () => {
  const { width } = useWindowDimensions();
  const isMobileIos = Platform.OS === 'ios' && width < 768;
  const dispatch = useDispatch();
  const { showToast } = useToast();

  const reportConfig = useSelector((state: RootState) =>
    state.reportingConfiguration.rows.find((row: { link?: string; reportKey?: string; }) => {
      if (String(row.link ?? '').trim().toLowerCase() === REPORT_LINK) {
        return true;
      }

      return normalizeFilterValue(row.reportKey) === 'order-rules';
    })
  );
  const currentPeriodWeek = useSelector((state: RootState) => state.appData?.periodWeek?.data);
  const headerSelectedLocation = useSelector((state: RootState) => state.appData.selectedLocation);

  // ── Filter-data from Redux (customers + materials from /api/order-rules/filter-data) ──
  const filterDataCustomers = useSelector((state: RootState) => state.orderRulesFilterData.customers);
  const filterDataMaterials = useSelector((state: RootState) => state.orderRulesFilterData.materials);
  const filterDataFetchedAt = useSelector((state: RootState) => state.orderRulesFilterData.fetchedAt);

  // ── Autocomplete debounce ref ─────────────────────────────────────────────
  const autocompleteTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  /**
   * Build a debounced search handler for a single autocomplete field.
   * Fires the POST /api/order-rules/autocomplete/filter-data endpoint when
   * the user has typed ≥ 3 characters, then merges new records into Redux.
   */
  const makeAutocompleteHandler = useCallback(
    (payloadKey: 'customerId' | 'customerName' | 'keyAccount' | 'materialId' | 'materialName' | 'packageName') =>
      (text: string) => {
        if (autocompleteTimerRef.current) {
          clearTimeout(autocompleteTimerRef.current);
        }
        if (text.length < 3) return;
        autocompleteTimerRef.current = setTimeout(() => {
          fetchOrderRulesFilterDataAutocomplete({ [payloadKey]: text } as any)
            .then((result) => {
              if (result.customers && result.customers.length > 0) {
                dispatch(appendOrderRulesFilterCustomers(result.customers));
              }
              if (result.materials && result.materials.length > 0) {
                dispatch(appendOrderRulesFilterMaterials(result.materials));
              }
            })
            .catch(() => {
              // Autocomplete errors are non-critical — silently ignored.
            });
        }, 300);
      },
    [dispatch],
  );

  const handleCustomerBpSearch   = useMemo(() => makeAutocompleteHandler('customerId'),   [makeAutocompleteHandler]);
  const handleCustomerSearch     = useMemo(() => makeAutocompleteHandler('customerName'),  [makeAutocompleteHandler]);
  const handleKeyAccountSearch   = useMemo(() => makeAutocompleteHandler('keyAccount'),    [makeAutocompleteHandler]);
  const handlePackageSearch      = useMemo(() => makeAutocompleteHandler('packageName'),   [makeAutocompleteHandler]);
  const handleMaterialNumberSearch = useMemo(() => makeAutocompleteHandler('materialId'),  [makeAutocompleteHandler]);
  const handleMaterialSearch     = useMemo(() => makeAutocompleteHandler('materialName'),  [makeAutocompleteHandler]);

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (autocompleteTimerRef.current) {
        clearTimeout(autocompleteTimerRef.current);
      }
    };
  }, []);

  const title = reportConfig?.name || 'Order Rules';
  const iconName = getValidIconName(reportConfig?.icon || 'rule');
  const subtitle =
    (reportConfig?.description || '').trim() ||
    'Order-rule execution, recommendation impact, and max-potential insights';

  // ── Last-refresh metadata per table group ────────────────────────────────
  // Tabs 1–4 share DBX_FACT_WEEKLY_AVG_QTY; tab 5 uses DBX_FACT_MAX_POTENTIAL;
  // tab 6 uses DBX_FACT_ORDERRULECHECK. We fetch each at most once and cache.
  const [weeklyAvgRefresh, setWeeklyAvgRefresh] = useState<OrderRulesLastRefreshData | null>(null);
  const [maxPotentialRefresh, setMaxPotentialRefresh] = useState<OrderRulesLastRefreshData | null>(null);
  const [orderRuleCheckRefresh, setOrderRuleCheckRefresh] = useState<OrderRulesLastRefreshData | null>(null);
  const weeklyAvgRefreshFetchedRef = React.useRef(false);
  const maxPotentialRefreshFetchedRef = React.useRef(false);
  const orderRuleCheckRefreshFetchedRef = React.useRef(false);

  const [orderRulesData, setOrderRulesData] = useState<OrderRulesDashboardResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [maxPotentialChartLoading, setMaxPotentialChartLoading] = useState(false);
  const [enrollmentTrendLoading, setEnrollmentTrendLoading] = useState(false);
  const [layerPerformanceLoading, setLayerPerformanceLoading] = useState(false);
  const [recommendationsLoading, setRecommendationsLoading] = useState(false);
  const [salesDetailsLoading, setSalesDetailsLoading] = useState(false);
  const [customerSkuLoading, setCustomerSkuLoading] = useState(false);
  const [orderRuleCheckLoading, setOrderRuleCheckLoading] = useState(false);
  const [maxPotentialTableLoading, setMaxPotentialTableLoading] = useState(false);
  const [apiErrorModal, setApiErrorModal] = useState<{ visible: boolean; title: string; message: string }>({
    visible: false,
    title: '',
    message: '',
  });

  const [openFilterKey, setOpenFilterKey] = useState<ExtendedFilterKey | null>(null);
  const [activeTab, setActiveTab] = useState<OrderRulesTab>('executive-overview');
  const [isMobileFilterOverlayVisible, setMobileFilterOverlayVisible] = useState(false);
  const [showSitesWithoutLayerLine, setShowSitesWithoutLayerLine] = useState(false);
  const [expandedMaxPotentialRows, setExpandedMaxPotentialRows] = useState<Record<string, boolean>>({});
  const [activeQuickTimeFrame, setActiveQuickTimeFrame] = useState<QuickTimeFrameKey | null>(null);
  const [execMaxPotentialViewMode, setExecMaxPotentialViewMode] = useState<'percent' | 'cases'>('percent');
  // Derive the header labels from the per-tab last-refresh state.
  const activeRefreshData: OrderRulesLastRefreshData | null = useMemo(() => {
    if (activeTab === 'max-potential') return maxPotentialRefresh;
    if (activeTab === 'order-rule-check') return orderRuleCheckRefresh;
    return weeklyAvgRefresh;
  }, [activeTab, weeklyAvgRefresh, maxPotentialRefresh, orderRuleCheckRefresh]);

  const lastRefreshedLabel = activeRefreshData?.lastRefreshed
    ? formatToLocalWithTZ(activeRefreshData.lastRefreshed)
    : '';

  const recommendationWindowLabel = activeRefreshData
    ? formatRecommendationWindow(
        activeRefreshData.recommendationStartWindow,
        activeRefreshData.recommendationEndWindow,
      )
    : '';

  // ── Pagination + server-side sort state for paginated tabs ────────────────
  // sortBy carries the backend field name; sortDirection is 'asc' | 'desc'.
  const DEFAULT_PAGE_SIZE = 25;
  type TabPagination = { page: number; size: number; sortBy?: string; sortDirection?: 'asc' | 'desc' };
  const [recoPagination, setRecoPagination] = useState<TabPagination>({ page: 0, size: DEFAULT_PAGE_SIZE });
  const [recoTotalPages, setRecoTotalPages] = useState(0);
  const [recoTotalElements, setRecoTotalElements] = useState(0);

  const [salesPagination, setSalesPagination] = useState<TabPagination>({ page: 0, size: DEFAULT_PAGE_SIZE });
  const [salesTotalPages, setSalesTotalPages] = useState(0);
  const [salesTotalElements, setSalesTotalElements] = useState(0);

  const [checkPagination, setCheckPagination] = useState<TabPagination>({ page: 0, size: DEFAULT_PAGE_SIZE });
  const [checkTotalPages, setCheckTotalPages] = useState(0);
  const [checkTotalElements, setCheckTotalElements] = useState(0);
  // Order Rule Check toggle — defaults to true (non-eligible SKUs included) and is sent
  // to the API as `showNonEligibleSkus` on every orderrule-check request.
  const [showNonEligibleSkus, setShowNonEligibleSkus] = useState(false);
  // Order Rule Check toggle — defaults to false and is sent to the API as `showReviewed`
  // on every orderrule-check request when enabled.
  const [showOnlyReviewed, setShowOnlyReviewed] = useState(false);
  // Order Rule Check toggle — mutually exclusive with showNonEligibleSkus; sent to the
  // API as `showNonInvoicedSkus` only when enabled, omitted entirely when off.
  const [showOnlyNonInvoicedSkus, setShowOnlyNonInvoicedSkus] = useState(false);

  const [maxPotPagination, setMaxPotPagination] = useState<TabPagination>({ page: 0, size: DEFAULT_PAGE_SIZE });
  const [maxPotTotalPages, setMaxPotTotalPages] = useState(0);
  const [maxPotTotalElements, setMaxPotTotalElements] = useState(0);

  const [skuUtilPagination, setSkuUtilPagination] = useState<TabPagination>({ page: 0, size: DEFAULT_PAGE_SIZE });
  const [skuUtilTotalPages, setSkuUtilTotalPages] = useState(0);
  const [skuUtilTotalElements, setSkuUtilTotalElements] = useState(0);
  // Customer & SKU Utilization tab — granularity values sent to the API, driven by the Columns dropdown.
  const [skuUtilGranularity, setSkuUtilGranularity] = useState<string[]>([]);

  const initialLocationFromHeader = useMemo<KPIFilterDropdownItem[]>(() => {
    if (!headerSelectedLocation) return [];
    const name = String(headerSelectedLocation.displayText || headerSelectedLocation.name || '').trim();
    if (!name) return [];
      return [{ id: String(headerSelectedLocation.locSapId || headerSelectedLocation.id || headerSelectedLocation.locationId || name), displayText: name }];    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const hasAutoPopulatedRef = React.useRef(initialLocationFromHeader.length > 0);
  const currentYear = useMemo(() => new Date().getFullYear(), []);

  const initialPeriodWeekFromCurrentWeek = useMemo(() => {
    if (!currentPeriodWeek?.period || !currentPeriodWeek?.week) {
      return null;
    }

    const previous = getPreviousPeriodAndWeek(currentPeriodWeek.period, currentPeriodWeek.week);
    const defaultPeriod = Number(previous.period);
    const defaultWeek = Number(previous.week);

    if (!Number.isFinite(defaultPeriod) || !Number.isFinite(defaultWeek)) {
      return null;
    }

    const weekInYearNum = (defaultPeriod - 1) * 4 + defaultWeek;
    return {
      period: defaultPeriod,
      week: defaultWeek,
      weekInYearNum,
    };
  }, [currentPeriodWeek]);

  const initialPeriodFromCurrentWeek = useMemo<KPIFilterDropdownItem[]>(() => {
    if (!initialPeriodWeekFromCurrentWeek) return [];
    return [{
      id: `P${initialPeriodWeekFromCurrentWeek.period}`,
      displayText: `P${initialPeriodWeekFromCurrentWeek.period}`,
    }];
  }, [initialPeriodWeekFromCurrentWeek]);

  const initialWeekFromCurrentWeek = useMemo<KPIFilterDropdownItem[]>(() => {
    if (!initialPeriodWeekFromCurrentWeek) return [];
    return [{
      id: `P${initialPeriodWeekFromCurrentWeek.period}W${initialPeriodWeekFromCurrentWeek.week}`,
      displayText: `W${initialPeriodWeekFromCurrentWeek.weekInYearNum} (P${initialPeriodWeekFromCurrentWeek.period}W${initialPeriodWeekFromCurrentWeek.week})`,
      weekInYearNum: initialPeriodWeekFromCurrentWeek.weekInYearNum,
    }];
  }, [initialPeriodWeekFromCurrentWeek]);

  const [selectedRegions, setSelectedRegions] = useState<KPIFilterDropdownItem[]>([]);
  const [selectedMarkets, setSelectedMarkets] = useState<KPIFilterDropdownItem[]>([]);
  const [selectedLocations, setSelectedLocations] = useState<KPIFilterDropdownItem[]>(() => initialLocationFromHeader);
  const [selectedCustomerBps, setSelectedCustomerBps] = useState<KPIFilterDropdownItem[]>([]);
  const [selectedCustomers, setSelectedCustomers] = useState<KPIFilterDropdownItem[]>([]);
  const [selectedKeyAccounts, setSelectedKeyAccounts] = useState<KPIFilterDropdownItem[]>([]);
  const [selectedPackages, setSelectedPackages] = useState<KPIFilterDropdownItem[]>([]);
  const [selectedMaterialNumbers, setSelectedMaterialNumbers] = useState<KPIFilterDropdownItem[]>([]);
  const [selectedMaterials, setSelectedMaterials] = useState<KPIFilterDropdownItem[]>([]);
  const [selectedYears, setSelectedYears] = useState<KPIFilterDropdownItem[]>([
    { id: String(currentYear), displayText: String(currentYear) },
  ]);
  const [selectedPeriods, setSelectedPeriods] = useState<KPIFilterDropdownItem[]>(() => initialPeriodFromCurrentWeek);
  const [selectedWeeks, setSelectedWeeks] = useState<KPIFilterDropdownItem[]>(() => initialWeekFromCurrentWeek);
  const [appliedFilterParams, setAppliedFilterParams] = useState<OrderRulesFilterParams>({
    year: String(currentYear),
  });
  const [rawPeriodWeeksData, setRawPeriodWeeksData] = useState<any[]>([]);

  const { locations: adminLocations, fetchedAt: adminLocationsFetchedAt } = useAdminLocations();

  const selectedRegionIds: string[] = useMemo(() => {
    if (selectedRegions.length === 0) return ['all'];
    return selectedRegions.map((r) => normalizeFilterValue(r.id));
  }, [selectedRegions]);

  const selectedMarketIds: string[] = useMemo(() => {
    if (selectedMarkets.length === 0) return ['all'];
    return selectedMarkets.map((m) => normalizeFilterValue(m.id));
  }, [selectedMarkets]);

  const regionOptions: KPIFilterDropdownItem[] = useMemo(() => {
    const seen = new Set<string>();
    const options: KPIFilterDropdownItem[] = [];
    adminLocations.forEach((loc: { region: string | null; }) => {
      const value = loc.region?.trim();
      if (value && !seen.has(value)) {
        seen.add(value);
        options.push({ id: value, displayText: value });
      }
    });
    return options.sort((a, b) => a.displayText.localeCompare(b.displayText));
  }, [adminLocations]);

  const marketOptions: KPIFilterDropdownItem[] = useMemo(() => {
    const seen = new Set<string>();
    const options: KPIFilterDropdownItem[] = [];

    adminLocations
      .filter(
        (loc: { region: unknown; }) =>
          selectedRegions.length === 0 ||
          selectedRegions.some((region) => normalizeFilterValue(region.id) === normalizeFilterValue(loc.region))
      )
      .forEach((loc: { market: string | null; }) => {
        const value = loc.market?.trim();
        if (value && !seen.has(value)) {
          seen.add(value);
          options.push({ id: value, displayText: value });
        }
      });

    return options.sort((a, b) => a.displayText.localeCompare(b.displayText));
  }, [adminLocations, selectedRegions]);

  const locationOptions: KPIFilterDropdownItem[] = useMemo(
    () =>
      adminLocations
        .filter((loc: { region: unknown; market: unknown; }) => {
          const matchesRegion =
            selectedRegionIds.includes('all') || selectedRegionIds.includes(normalizeFilterValue(loc.region));
          const matchesMarket =
            selectedMarketIds.includes('all') || selectedMarketIds.includes(normalizeFilterValue(loc.market));
          return matchesRegion && matchesMarket;
        })
        .map((loc: { locId: any; locName: any; }) => ({ id: loc.locId, displayText: loc.locName }))
        .sort((a: { displayText: string; }, b: { displayText: any; }) => a.displayText.localeCompare(b.displayText)),
    [adminLocations, selectedRegionIds, selectedMarketIds]
  );

  // ── Filter dropdown options — driven by /api/order-rules/filter-data (Redux) ──
  //
  // Cascading rules (customer group):
  //   • Selecting a Customer BP  → narrows Customer and Key Account to that customer's records
  //   • Selecting a Customer     → narrows Customer BP and Key Account to that customer's records
  //   • Selecting a Key Account  → narrows Customer BP and Customer to that key account's records
  //   Any two selections together further intersect.
  //
  // Cascading rules (material group):
  //   • Selecting a Package      → narrows Material No. and Material to that package's records
  //   • Selecting a Material No. → narrows Package and Material to that material's records
  //   • Selecting a Material     → narrows Package and Material No. to that material's records

  // Derive the active filter sets once so all six memos share the same references.
  const activeBpIds      = useMemo(() => new Set(selectedCustomerBps.map((i) => i.id)),      [selectedCustomerBps]);
  const activeCustIds    = useMemo(() => new Set(selectedCustomers.map((i) => i.id)),         [selectedCustomers]);
  const activeKeyAccts   = useMemo(() => new Set(selectedKeyAccounts.map((i) => i.id)),       [selectedKeyAccounts]);
  const activePkgIds     = useMemo(() => new Set(selectedPackages.map((i) => i.id)),          [selectedPackages]);
  const activeMatlIds    = useMemo(() => new Set(selectedMaterialNumbers.map((i) => i.id)),   [selectedMaterialNumbers]);
  const activeMatlNames  = useMemo(() => new Set(selectedMaterials.map((i) => i.id)),         [selectedMaterials]);

  // ── Six dropdown option lists, each scoped to the opposite two sibling filters ──

  // Customer BP options: restrict by selected Customer + Key Account (not by BP itself)
  const customerBpOptions: KPIFilterDropdownItem[] = useMemo(() => {
    const base = filterDataCustomers.filter((c: { customerId: string | number; keyAccountName: string | number; }) => {
      if (!c.customerId) return false;
      if (activeCustIds.size  > 0 && !activeCustIds.has(c.customerId))      return false;
      if (activeKeyAccts.size > 0 && !activeKeyAccts.has(c.keyAccountName)) return false;
      return true;
    });
    const seen = new Set<string>();
    return base
      .filter((c: { customerId: string; }) => { if (seen.has(c.customerId)) return false; seen.add(c.customerId); return true; })
      .map((c: { customerId: any; }) => ({ id: c.customerId, displayText: c.customerId }))
      .sort((a: { displayText: string; }, b: { displayText: any; }) => a.displayText.localeCompare(b.displayText));
  }, [filterDataCustomers, activeCustIds, activeKeyAccts]);

  // Customer options: restrict by selected Customer BP + Key Account (not by Customer itself)
  const customerOptions: KPIFilterDropdownItem[] = useMemo(() => {
    const base = filterDataCustomers.filter((c: { customerDisplayName: any; customerId: string | number; keyAccountName: string | number; }) => {
      if (!c.customerDisplayName) return false;
      if (activeBpIds.size    > 0 && !activeBpIds.has(c.customerId))        return false;
      if (activeKeyAccts.size > 0 && !activeKeyAccts.has(c.keyAccountName)) return false;
      return true;
    });
    const seen = new Set<string>();
    return base
      .filter((c: { customerId: string; }) => { if (seen.has(c.customerId)) return false; seen.add(c.customerId); return true; })
      .map((c: { customerId: any; customerDisplayName: any; }) => ({ id: c.customerId, displayText: c.customerDisplayName }))
      .sort((a: { displayText: string; }, b: { displayText: any; }) => a.displayText.localeCompare(b.displayText));
  }, [filterDataCustomers, activeBpIds, activeKeyAccts]);

  // Key Account options: restrict by selected Customer BP + Customer (not by Key Account itself)
  const keyAccountOptions: KPIFilterDropdownItem[] = useMemo(() => {
    const base = filterDataCustomers.filter((c: { keyAccountName: any; customerId: string | number; }) => {
      if (!c.keyAccountName) return false;
      if (activeBpIds.size   > 0 && !activeBpIds.has(c.customerId))   return false;
      if (activeCustIds.size > 0 && !activeCustIds.has(c.customerId)) return false;
      return true;
    });
    const seen = new Set<string>();
    return base
      .filter((c: { keyAccountName: string; }) => { if (seen.has(c.keyAccountName)) return false; seen.add(c.keyAccountName); return true; })
      .map((c: { keyAccountName: any; }) => ({ id: c.keyAccountName, displayText: c.keyAccountName }))
      .sort((a: { displayText: string; }, b: { displayText: any; }) => a.displayText.localeCompare(b.displayText));
  }, [filterDataCustomers, activeBpIds, activeCustIds]);

  // Package options: restrict by selected Material No. + Material (not by Package itself)
  const packageOptions: KPIFilterDropdownItem[] = useMemo(() => {
    const base = filterDataMaterials.filter((m: { packageName: any; materialId: string | number; }) => {
      if (!m.packageName) return false;
      if (activeMatlIds.size   > 0 && !activeMatlIds.has(m.materialId))   return false;
      if (activeMatlNames.size > 0 && !activeMatlNames.has(m.materialId)) return false;
      return true;
    });
    const seen = new Set<string>();
    return base
      .filter((m: { packageId: string; }) => { if (seen.has(m.packageId)) return false; seen.add(m.packageId); return true; })
      .map((m: { packageId: any; packageName: any; }) => ({ id: m.packageId, displayText: m.packageName }))
      .sort((a: { displayText: string; }, b: { displayText: any; }) => a.displayText.localeCompare(b.displayText));
  }, [filterDataMaterials, activeMatlIds, activeMatlNames]);

  // Material No. options: restrict by selected Package + Material (not by Material No. itself)
  const materialNumberOptions: KPIFilterDropdownItem[] = useMemo(() => {
    const base = filterDataMaterials.filter((m: { materialId: string | number; packageId: string | number; }) => {
      if (!m.materialId) return false;
      if (activePkgIds.size    > 0 && !activePkgIds.has(m.packageId))    return false;
      if (activeMatlNames.size > 0 && !activeMatlNames.has(m.materialId)) return false;
      return true;
    });
    const seen = new Set<string>();
    return base
      .filter((m: { materialId: string; }) => { if (seen.has(m.materialId)) return false; seen.add(m.materialId); return true; })
      .map((m: { materialId: any; }) => ({ id: m.materialId, displayText: m.materialId }))
      .sort((a: { displayText: string; }, b: { displayText: any; }) => a.displayText.localeCompare(b.displayText));
  }, [filterDataMaterials, activePkgIds, activeMatlNames]);

  // Material options: restrict by selected Package + Material No. (not by Material itself)
  const materialOptions: KPIFilterDropdownItem[] = useMemo(() => {
    const base = filterDataMaterials.filter((m: { materialName: any; packageId: string | number; materialId: string | number; }) => {
      if (!m.materialName) return false;
      if (activePkgIds.size  > 0 && !activePkgIds.has(m.packageId))  return false;
      if (activeMatlIds.size > 0 && !activeMatlIds.has(m.materialId)) return false;
      return true;
    });
    const seen = new Set<string>();
    return base
      .filter((m: { materialId: string; }) => { if (seen.has(m.materialId)) return false; seen.add(m.materialId); return true; })
      .map((m: { materialId: any; materialName: any; }) => ({ id: m.materialId, displayText: m.materialName }))
      .sort((a: { displayText: string; }, b: { displayText: any; }) => a.displayText.localeCompare(b.displayText));
  }, [filterDataMaterials, activePkgIds, activeMatlIds]);

  useEffect(() => {
    if (!locationOptions || locationOptions.length === 0) return;

    setSelectedLocations((current) => {
      if (!current || current.length === 0) return current;

      const validItems = current.filter((selectedLoc) =>
        locationOptions.some((opt) => String(opt.id) === String(selectedLoc.id))
      );
      if (validItems.length > 0) return validItems;

      const selected = current[0];
      const match = locationOptions.find(
        (opt) =>
          String(opt.id) === String((selected as any).locationId) ||
          normalizeFilterValue(opt.displayText) === normalizeFilterValue(selected.displayText) ||
          normalizeFilterValue(opt.displayText) === normalizeFilterValue((selected as any).name || '')
      );
      if (match) return [match];

      return [];
    });
  }, [locationOptions]);

  useEffect(() => {
    if (hasAutoPopulatedRef.current) return;
    if (!headerSelectedLocation) return;
    if (!locationOptions || locationOptions.length === 0) return;

    const locId = String(headerSelectedLocation.id || headerSelectedLocation.locationId || '');
    const locName = headerSelectedLocation.displayText || headerSelectedLocation.name || '';
    const matchingOption = locationOptions.find(
      (opt) => String(opt.id) === locId || normalizeFilterValue(opt.displayText) === normalizeFilterValue(locName)
    );

    if (matchingOption) {
      setSelectedLocations([matchingOption]);
      hasAutoPopulatedRef.current = true;
    }
  }, [headerSelectedLocation, locationOptions]);

  const FIRST_ANALYTICS_YEAR = 2025;
  const yearOptions: KPIFilterDropdownItem[] = useMemo(() => {
    const count = Math.max(1, currentYear - FIRST_ANALYTICS_YEAR + 1);
    return Array.from({ length: count }, (_, index) => ({
      id: String(currentYear - index),
      displayText: String(currentYear - index),
    }));
  }, [currentYear]);

  const selectedYearNumbers = useMemo(
    () =>
      selectedYears.length > 0
        ? selectedYears.map((year) => Number(year.id)).filter((value) => !Number.isNaN(value))
        : [],
    [selectedYears]
  );

  // Fetch /api/order-rules/filter-data once on mount (skip if already loaded in Redux).
  useEffect(() => {
    if (filterDataFetchedAt != null) return; // already in store — no re-fetch needed

    dispatch(setOrderRulesFilterDataLoading());
    fetchOrderRulesFilterData()
      .then((data) => {
        dispatch(setOrderRulesFilterData({
          customers: data.customers ?? [],
          materials: data.materials ?? [],
        }));
      })
      .catch((err: any) => {
        const message = err?.response?.data?.message || err?.message || 'Failed to load filter data.';
        dispatch(setOrderRulesFilterDataError(message));
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let isMounted = true;

    if (selectedYearNumbers.length === 0) {
      setRawPeriodWeeksData([]);
      return () => {
        isMounted = false;
      };
    }

    fetchPeriodWeeks(selectedYearNumbers)
      .then((response) => {
        if (isMounted) {
          setRawPeriodWeeksData(response.data ?? []);
        }
      })
      .catch(() => {
        if (isMounted) {
          setRawPeriodWeeksData([]);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [selectedYearNumbers]);

  const periodOptions: KPIFilterDropdownItem[] = useMemo(() => {
    if (rawPeriodWeeksData.length === 0) {
      return Array.from({ length: 13 }, (_, index) => ({ id: `P${index + 1}`, displayText: `P${index + 1}` }));
    }

    const periodSet = new Set<number>();
    rawPeriodWeeksData.forEach((yearData: any) => {
      (yearData.periods ?? []).forEach((periodData: any) => {
        if (periodData.period != null) periodSet.add(Number(periodData.period));
      });
    });

    return Array.from(periodSet)
      .sort((left, right) => left - right)
      .map((period) => ({ id: `P${period}`, displayText: `P${period}` }));
  }, [rawPeriodWeeksData]);

  const weekOptions: KPIFilterDropdownItem[] = useMemo(() => {
    if (rawPeriodWeeksData.length === 0) {
      if (selectedYearNumbers.length === 0 && selectedPeriods.length === 0) {
        return Array.from({ length: 52 }, (_, index) => {
          const weekInYear = index + 1;
          return {
            id: `W${weekInYear}`,
            displayText: `W${weekInYear}`,
            weekInYearNum: weekInYear,
          };
        });
      }

      return Array.from({ length: 4 }, (_, index) => {
        const weekInYear = index + 1;
        return {
          id: `W${weekInYear}`,
          displayText: `W${weekInYear}`,
          weekInYearNum: weekInYear,
        };
      });
    }

    const items: KPIFilterDropdownItem[] = [];

    rawPeriodWeeksData.forEach((yearData: any) => {
      (yearData.periods ?? []).forEach((periodData: any) => {
        const periodNum = Number(periodData.period);

        (periodData.weeks ?? []).forEach((week: any, index: number) => {
          const weekInYear = typeof week === 'number' ? week : Number(week?.weekInYear ?? week?.week ?? index + 1);
          const weekInPeriod = index + 1;

          items.push({
            id: `P${periodNum}W${weekInPeriod}`,
            displayText: `W${weekInYear} (P${periodNum}W${weekInPeriod})`,
            weekInYearNum: weekInYear,
          });
        });
      });
    });

    const uniqueMap = new Map<string, KPIFilterDropdownItem>();
    items.forEach((item) => {
      const key = String(item.id);
      if (!uniqueMap.has(key)) uniqueMap.set(key, item);
    });

    return Array.from(uniqueMap.values()).sort((left, right) => {
      const leftNum = Number((left as any).weekInYearNum) || 0;
      const rightNum = Number((right as any).weekInYearNum) || 0;
      return leftNum - rightNum;
    });
  }, [rawPeriodWeeksData, selectedYearNumbers]);

  const { disabledPeriodIds } = useDisabledPeriodWeekIds({
    currentPeriodWeek,
    selectedYearNumbers,
    periodOptions,
    weekOptions,
  });

  const disabledWeekIds = useMemo(() => {
    if (!currentPeriodWeek?.year || !currentPeriodWeek?.period || !currentPeriodWeek?.week) return new Set<string>();
    if (selectedYearNumbers.length !== 1 || selectedYearNumbers[0] !== currentPeriodWeek.year) return new Set<string>();

    const currentWeekInYear = (currentPeriodWeek.period - 1) * 4 + currentPeriodWeek.week;
    const disabled = new Set<string>();
    for (const option of weekOptions) {
      const weekInYearNum = Number((option as any).weekInYearNum);
      if (Number.isFinite(weekInYearNum) && weekInYearNum > currentWeekInYear) {
        disabled.add(String(option.id));
      }
    }
    return disabled;
  }, [currentPeriodWeek, selectedYearNumbers, weekOptions]);

  const currentFilters = useMemo<OrderRulesFilterParams>(() => {
    const params: OrderRulesFilterParams = {};

    if (selectedLocations.length > 0 && selectedLocations.length < locationOptions.length) {
      params.location = selectedLocations.map((location) => location.displayText).join(',');
    } else if (selectedMarkets.length > 0) {
      params.market = selectedMarkets.map((market) => String(market.id)).join(',');
      if (selectedRegions.length > 0) {
        params.region = selectedRegions.map((region) => String(region.id)).join(',');
      }
    } else if (selectedRegions.length > 0) {
      params.region = selectedRegions.map((region) => String(region.id)).join(',');
    }

    if (selectedYears.length > 0) {
      params.year = selectedYears.map((year) => String(year.id)).join(',');
    }

    if (selectedCustomerBps.length > 0) {
      params.customerBp = selectedCustomerBps.map((item) => String(item.id)).join(',');
    }

    if (selectedCustomers.length > 0) {
      params.customer = selectedCustomers.map((item) => String(item.id)).join(',');
      params.customerName = selectedCustomers.map((item) => String(item.displayText)).join(',');
    }

    if (selectedKeyAccounts.length > 0) {
      params.keyAccount = selectedKeyAccounts.map((item) => String(item.id)).join(',');
    }

    if (selectedPackages.length > 0) {
      params.package = selectedPackages.map((item) => String(item.id)).join(',');
      params.packageName = selectedPackages.map((item) => String(item.displayText)).join(',');
    }

    if (selectedMaterialNumbers.length > 0) {
      params.materialNumber = selectedMaterialNumbers.map((item) => String(item.id)).join(',');
    }

    if (selectedMaterials.length > 0) {
      params.material = selectedMaterials.map((item) => String(item.id)).join(',');
      params.materialName = selectedMaterials.map((item) => String(item.displayText)).join(',');
    }

    if (selectedWeeks.length > 0) {
      const periodsFromWeeks = new Set<string>();
      // Send the actual WK_IN_FSCL_YR (weekInYearNum), not the week-in-period ordinal.
      const weekInYearValues: string[] = [];

      selectedWeeks.forEach((week) => {
        const match = String(week.displayText).match(/\(P(\d+)W(\d+)\)/);
        if (match) {
          periodsFromWeeks.add(match[1]);
        }
        const weekInYearNum = (week as any).weekInYearNum;
        if (weekInYearNum != null) {
          weekInYearValues.push(String(weekInYearNum));
        }
      });

      if (weekInYearValues.length > 0) {
        params.week = weekInYearValues.join(',');
        const explicitPeriods = selectedPeriods.map((period) => String(period.id).replace(/^P/i, ''));
        const allPeriods = Array.from(new Set([...explicitPeriods, ...periodsFromWeeks]));
        if (allPeriods.length > 0) {
          params.period = allPeriods.join(',');
        }
      }
    } else if (selectedPeriods.length > 0) {
      params.period = selectedPeriods.map((period) => String(period.id).replace(/^P/i, '')).join(',');
    }

    return params;
  }, [
    selectedLocations,
    locationOptions,
    selectedMarkets,
    selectedRegions,
    selectedYears,
    selectedWeeks,
    selectedPeriods,
    selectedCustomerBps,
    selectedCustomers,
    selectedKeyAccounts,
    selectedPackages,
    selectedMaterialNumbers,
    selectedMaterials,
  ]);

  const isApplyEnabled = useMemo(() => {
    const normalize = (params: OrderRulesFilterParams) => {
      const output: Record<string, string[]> = {};
      Object.keys(params).forEach((key) => {
        const value = (params as any)[key];
        if (value == null || value === '') return;
        output[key] = String(value)
          .split(',')
          .map((item) => item.trim())
          .filter((item) => item !== '')
          .sort();
      });
      return output;
    };

    const next = normalize(currentFilters);
    const applied = normalize(appliedFilterParams || {});
    const nextKeys = Object.keys(next).sort();
    const appliedKeys = Object.keys(applied).sort();
    if (nextKeys.length !== appliedKeys.length) return true;

    for (let index = 0; index < nextKeys.length; index++) {
      if (nextKeys[index] !== appliedKeys[index]) return true;
      const nextValues = next[nextKeys[index]];
      const appliedValues = applied[appliedKeys[index]];
      if (nextValues.length !== appliedValues.length) return true;
      for (let valueIndex = 0; valueIndex < nextValues.length; valueIndex++) {
        if (nextValues[valueIndex] !== appliedValues[valueIndex]) return true;
      }
    }

    return false;
  }, [currentFilters, appliedFilterParams]);

  const showApiError = (errorTitle: string, message: string) => {
    setApiErrorModal({ visible: true, title: errorTitle, message });
  };

  const dismissApiError = () => {
    setApiErrorModal((current) => ({ ...current, visible: false }));
  };

  /**
   * Resolve the Loc_SAP_ID (locSapId) for each selected location.
   *
   * Selected locations can be seeded from two sources:
   *   1. The header's selectedLocation (uses internal integer id e.g. "3948")
   *   2. The locationOptions dropdown (uses locId e.g. "1173")
   *
   * We look up each selection in adminLocations — matching by locId,
   * then by internal numeric id, then by display name — and always return
   * the locSapId of the matched entry, since that is the value the backend
   * OrderRulesFilterRequest.locationIds field expects.
   * If a match isn't found (adminLocations not yet loaded) we still include
   * the raw id so the call proceeds; it will be corrected on the next render
   * once the reconciliation effect has swapped in the proper locId-based item.
   */
  const getLocationIds = useCallback(
    (locations: KPIFilterDropdownItem[]): string[] => {
      return locations
        .map((selected) => {
          const selectedId = String(selected.id);

          // 1. Exact locId match — most common path after the reconciliation
          //    effect has resolved the header location.
          const byLocId = adminLocations.find((al: { locId: string; }) => al.locId === selectedId);
          if (byLocId?.locSapId) return byLocId.locSapId;

          // 2. Internal numeric id match — header-seeded selection before
          //    adminLocations have finished loading / reconciling.
          const byInternalId = adminLocations.find(
            (al: { id: any; }) => String(al.id) === selectedId,
          );
          if (byInternalId?.locSapId) return byInternalId.locSapId;

          // 3. Display-name fallback.
          const byName = adminLocations.find(
            (al: { locName: unknown; }) =>
              normalizeFilterValue(al.locName) ===
              normalizeFilterValue(selected.displayText),
          );
          if (byName?.locSapId) return byName.locSapId;

          // 4. Nothing found yet — return the raw id as a best-effort value.
          return selectedId;
        })
        .filter((id) => id.length > 0);
    },
    [adminLocations],
  );

  /**
   * Load Executive Overview data in a partial-render friendly way:
   * each API updates the dashboard state as soon as its response resolves,
   * rather than waiting for the whole batch to complete before any card/chart renders.
   */
  const loadExecutiveOverview = useCallback(
    (params: OrderRulesFilterParams, locationIds: string[]) => {
      const mergePartial = (partial: Partial<OrderRulesDashboardResponse>) => {
        setOrderRulesData((current) => ({
          ...(current ?? ({} as OrderRulesDashboardResponse)),
          ...partial,
        } as OrderRulesDashboardResponse));
      };

      void (async () => {
        setSummaryLoading(true);
        try {
          const summaryRes = await fetchOrderRulesSummary(params, locationIds);
          mergePartial(normalizeSummaryResponse(summaryRes));
        } catch (error: any) {
          const message =
            error?.response?.data?.message || error?.message || 'Failed to load Order Rules summary.';
          showApiError('Order Rules Load Failed', message);
        } finally {
          setSummaryLoading(false);
        }
      })();

      void (async () => {
        setMaxPotentialChartLoading(true);
        try {
          const maxPotChartRes = await fetchOrderRulesChartMaxPotential(params, locationIds);
          mergePartial(normalizeMaxPotentialChartResponse(maxPotChartRes));
        } catch (error: any) {
          const message =
            error?.response?.data?.message || error?.message || 'Failed to load max potential chart.';
          showApiError('Order Rules Load Failed', message);
        } finally {
          setMaxPotentialChartLoading(false);
        }
      })();

      void (async () => {
        setEnrollmentTrendLoading(true);
        try {
          const enrollmentRes = await fetchOrderRulesChartEnrollmentTrend(params, locationIds);
          mergePartial(normalizeEnrollmentTrendResponse(enrollmentRes));
        } catch (error: any) {
          const message =
            error?.response?.data?.message || error?.message || 'Failed to load enrollment trend.';
          showApiError('Order Rules Load Failed', message);
        } finally {
          setEnrollmentTrendLoading(false);
        }
      })();

      void (async () => {
        setLayerPerformanceLoading(true);
        try {
          const layerPerfRes = await fetchOrderRulesChartLayerPerformance(params, locationIds);
          mergePartial(normalizeLayerPerformanceResponse(layerPerfRes));
        } catch (error: any) {
          const message =
            error?.response?.data?.message || error?.message || 'Failed to load layer performance.';
          showApiError('Order Rules Load Failed', message);
        } finally {
          setLayerPerformanceLoading(false);
        }
      })();
    },
    [],
  );

  /**
   * Load Recommendations tab data (paginated).
   */
  const loadRecommendations = useCallback(
    async (
      params: OrderRulesFilterParams,
      locationIds: string[],
      pagination: { page: number; size: number; sortBy?: string; sortDirection?: 'asc' | 'desc' },
    ) => {
      setRecommendationsLoading(true);
      try {
        // sortBy must be the backend's field name (e.g. materialNo, customerName),
        // not the UI column key used for header highlighting.
        const apiPagination = {
          ...pagination,
          sortBy: pagination.sortBy ? RECO_SORT_FIELD_MAP[pagination.sortBy] ?? pagination.sortBy : undefined,
        };
        const res = await fetchOrderRulesRecommendations(params, locationIds, apiPagination);
        const normalized = normalizeRecommendationsResponse(res);
        setOrderRulesData((current) => ({
          ...(current ?? ({} as OrderRulesDashboardResponse)),
          ...normalized,
        } as OrderRulesDashboardResponse));
        setRecoTotalPages(res.totalPages ?? 0);
        setRecoTotalElements(res.totalElements ?? 0);
      } finally {
        setRecommendationsLoading(false);
      }
    },
    [],
  );

  /**
   * Load Sales Details tab data (paginated).
   */
  const loadSalesDetails = useCallback(
    async (
      params: OrderRulesFilterParams,
      locationIds: string[],
      pagination: { page: number; size: number; sortBy?: string; sortDirection?: 'asc' | 'desc' },
    ) => {
      setSalesDetailsLoading(true);
      try {
        const apiPagination = {
          ...pagination,
          sortBy: pagination.sortBy ? SALES_SORT_FIELD_MAP[pagination.sortBy] ?? pagination.sortBy : undefined,
        };
        const res = await fetchOrderRulesSalesVolume(params, locationIds, apiPagination);
        const normalized = normalizeSalesVolumeResponse(res);
        setOrderRulesData((current) => ({
          ...(current ?? ({} as OrderRulesDashboardResponse)),
          ...normalized,
        } as OrderRulesDashboardResponse));
        setSalesTotalPages(res.totalPages ?? 0);
        setSalesTotalElements(res.totalElements ?? 0);
      } finally {
        setSalesDetailsLoading(false);
      }
    },
    [],
  );

  /**
   * Load Customer & SKU Utilization tab data (paginated).
   */
  const loadCustomerSkuUtilization = useCallback(
    async (
      params: OrderRulesFilterParams,
      locationIds: string[],
      pagination: { page: number; size: number; sortBy?: string; sortDirection?: 'asc' | 'desc' },
      granularity?: string[],
    ) => {
      setCustomerSkuLoading(true);
      try {
        const apiPagination = {
          ...pagination,
          sortBy: pagination.sortBy ? SKU_UTIL_SORT_FIELD_MAP[pagination.sortBy] ?? pagination.sortBy : undefined,
        };
        const res = await fetchCustomerSkuUtilization(params, locationIds, apiPagination, granularity);
        const normalized = normalizeCustomerSkuUtilizationResponse(res);
        setOrderRulesData((current) => ({
          ...(current ?? ({} as OrderRulesDashboardResponse)),
          ...normalized,
        } as OrderRulesDashboardResponse));
        setSkuUtilTotalPages(res.totalPages ?? 0);
        setSkuUtilTotalElements(res.totalElements ?? 0);
      } finally {
        setCustomerSkuLoading(false);
      }
    },
    [],
  );

  /**
   * Load Order Rule Check tab data (paginated).
   */
  const loadOrderRuleCheck = useCallback(
    async (
      params: OrderRulesFilterParams,
      locationIds: string[],
      pagination: { page: number; size: number },
      showNonEligible: boolean,
      showReviewed: boolean,
      showNonInvoiced: boolean,
    ) => {
      setOrderRuleCheckLoading(true);
      try {
        const res = await fetchOrderRulesCheck(params, locationIds, pagination, {
          showNonEligibleSkus: showNonEligible,
          showReviewed,
          showNonInvoicedSkus: showNonInvoiced,
        });
        const normalized = normalizeOrderRuleCheckResponse(res);
        setOrderRulesData((current) => ({
          ...(current ?? ({} as OrderRulesDashboardResponse)),
          ...normalized,
        } as OrderRulesDashboardResponse));
        setCheckTotalPages(res.totalPages ?? 0);
        setCheckTotalElements(res.totalElements ?? 0);
      } finally {
        setOrderRuleCheckLoading(false);
      }
    },
    [],
  );

  /**
   * Load Max Potential Execution tab data (paginated).
   */
  const loadMaxPotentialExecution = useCallback(
    async (
      params: OrderRulesFilterParams,
      locationIds: string[],
      pagination: { page: number; size: number; sortBy?: string; sortDirection?: 'asc' | 'desc' },
    ) => {
      setMaxPotentialTableLoading(true);
      try {
        const apiPagination = {
          ...pagination,
          sortBy: pagination.sortBy ? MAX_POT_SORT_FIELD_MAP[pagination.sortBy] ?? pagination.sortBy : undefined,
        };
        const res = await fetchOrderRulesMaxPotentialExecution(params, locationIds, apiPagination);
        const normalized = normalizeMaxPotentialExecutionResponse(res);
        setOrderRulesData((current) => ({
          ...(current ?? ({} as OrderRulesDashboardResponse)),
          ...normalized,
        } as OrderRulesDashboardResponse));
        setMaxPotTotalPages(res.totalPages ?? 0);
        setMaxPotTotalElements(res.totalElements ?? 0);
      } finally {
        setMaxPotentialTableLoading(false);
      }
    },
    [],
  );

  const lastLoadRequestRef = React.useRef<string | null>(null);

  // Track the applied filter params key that was last successfully *fetched* for each tab.
  // When the user returns to a tab we compare against the current appliedFilterParams and
  // re-fetch if they differ (i.e. filters were changed on another tab).
  const lastFetchedParamsByTab = React.useRef<Partial<Record<OrderRulesTab, string>>>({});

  const buildLoadKey = useCallback(
    (
      tab: OrderRulesTab,
      params: OrderRulesFilterParams,
      locationIds: string[],
      pagination?: { reco?: { page: number; size: number }; sales?: { page: number; size: number }; check?: { page: number; size: number }; maxPot?: { page: number; size: number }; skuUtil?: { page: number; size: number } },
      checkToggles?: { showNonEligibleSkus: boolean; showOnlyReviewed: boolean; showOnlyNonInvoicedSkus: boolean },
      granularity?: string[],
    ) => {
      const normalizedPagination = pagination ?? {
        reco: recoPagination,
        sales: salesPagination,
        check: checkPagination,
        maxPot: maxPotPagination,
        skuUtil: skuUtilPagination,
      };

      return JSON.stringify({
        tab,
        params,
        locationIds,
        pagination: normalizedPagination,
        showNonEligibleSkus: checkToggles?.showNonEligibleSkus ?? showNonEligibleSkus,
        showOnlyReviewed: checkToggles?.showOnlyReviewed ?? showOnlyReviewed,
        showOnlyNonInvoicedSkus: checkToggles?.showOnlyNonInvoicedSkus ?? showOnlyNonInvoicedSkus,
        skuUtilGranularity: granularity ?? skuUtilGranularity,
      });
    },
    [checkPagination, maxPotPagination, recoPagination, salesPagination, skuUtilPagination, showNonEligibleSkus, showOnlyReviewed, showOnlyNonInvoicedSkus, skuUtilGranularity],
  );

  /**
   * Master load function — fires the appropriate API(s) for the active tab.
   * Executive Overview triggers summary + all 3 charts in parallel.
   * All other tabs hit their dedicated endpoint with current pagination.
   */
  const loadDashboard = useCallback(
    async (
      params: OrderRulesFilterParams,
      tab: OrderRulesTab = activeTab,
      overridePagination?: { reco?: { page: number; size: number }; sales?: { page: number; size: number }; check?: { page: number; size: number }; maxPot?: { page: number; size: number }; skuUtil?: { page: number; size: number } },
      overrideCheckToggles?: { showNonEligibleSkus: boolean; showOnlyReviewed: boolean; showOnlyNonInvoicedSkus: boolean },
      overrideGranularity?: string[],
    ) => {
      const locationIds = getLocationIds(selectedLocations);
      const requestKey = buildLoadKey(tab, params, locationIds, overridePagination, overrideCheckToggles, overrideGranularity);

      // Prevent duplicate fetches when the mount effect and the adminLocations
      // reconciliation effect both resolve the same initial location state.
      if (lastLoadRequestRef.current === requestKey) {
        return;
      }
      lastLoadRequestRef.current = requestKey;

      setLoading(true);
      try {
        if (tab === 'executive-overview') {
          await loadExecutiveOverview(params, locationIds);
        } else if (tab === 'recommendations') {
          const pagination = overridePagination?.reco ?? recoPagination;
          await loadRecommendations(params, locationIds, pagination);
        } else if (tab === 'sales-details') {
          const pagination = overridePagination?.sales ?? salesPagination;
          await loadSalesDetails(params, locationIds, pagination);
        } else if (tab === 'customer-sku-utilization') {
          const pagination = overridePagination?.skuUtil ?? skuUtilPagination;
          await loadCustomerSkuUtilization(params, locationIds, pagination, overrideGranularity ?? skuUtilGranularity);
        } else if (tab === 'order-rule-check') {
          const pagination = overridePagination?.check ?? checkPagination;
          // Use the toggle values passed in for this call (not the possibly-stale state)
          // so an in-flight toggle change is reflected on the very next request.
          await loadOrderRuleCheck(
            params,
            locationIds,
            pagination,
            overrideCheckToggles?.showNonEligibleSkus ?? showNonEligibleSkus,
            overrideCheckToggles?.showOnlyReviewed ?? showOnlyReviewed,
            overrideCheckToggles?.showOnlyNonInvoicedSkus ?? showOnlyNonInvoicedSkus,
          );
        } else if (tab === 'max-potential') {
          const pagination = overridePagination?.maxPot ?? maxPotPagination;
          await loadMaxPotentialExecution(params, locationIds, pagination);
        }
        // Record the params fingerprint for this tab so returning to it later
        // can detect whether the global filters have changed in the interim.
        lastFetchedParamsByTab.current[tab] = JSON.stringify(params);
      } catch (error: any) {
        const message =
          error?.response?.data?.message || error?.message || 'An unexpected error occurred while loading Order Rules.';
        showApiError('Order Rules Load Failed', message);
      } finally {
        setLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [activeTab, selectedLocations, recoPagination, salesPagination, checkPagination, maxPotPagination, skuUtilPagination, showNonEligibleSkus, showOnlyReviewed, showOnlyNonInvoicedSkus, skuUtilGranularity, getLocationIds, buildLoadKey, loadExecutiveOverview, loadRecommendations, loadSalesDetails, loadCustomerSkuUtilization, loadOrderRuleCheck, loadMaxPotentialExecution],
  );

  // Fires the very first Executive Overview load exactly once, waiting for adminLocations
  // to finish loading so the header-seeded location resolves to its correct Loc_SAP_ID
  // (via getLocationIds) on that first request instead of triggering a second reload later.
  const initialLoadFiredRef = React.useRef(false);
  useEffect(() => {
    if (initialLoadFiredRef.current) return;
    if (adminLocationsFetchedAt == null) return;
    initialLoadFiredRef.current = true;

    const initParams: OrderRulesFilterParams = { year: String(currentYear) };
    if (initialLocationFromHeader.length > 0) {
      initParams.location = initialLocationFromHeader.map((location) => location.displayText).join(',');
    }

    if (currentPeriodWeek?.period && currentPeriodWeek?.week) {
      const previous = getPreviousPeriodAndWeek(currentPeriodWeek.period, currentPeriodWeek.week);
      const defaultPeriod = Number(previous.period);
      const defaultWeek = Number(previous.week);

      if (Number.isFinite(defaultPeriod) && Number.isFinite(defaultWeek)) {
        const defaultWeekInYear = (defaultPeriod - 1) * 4 + defaultWeek;
        setSelectedPeriods([{ id: `P${defaultPeriod}`, displayText: `P${defaultPeriod}` }]);
        setSelectedWeeks([{
          id: `P${defaultPeriod}W${defaultWeek}`,
          displayText: `W${defaultWeekInYear} (P${defaultPeriod}W${defaultWeek})`,
          weekInYearNum: defaultWeekInYear,
        }]);
        initParams.period = String(defaultPeriod);
        initParams.week = String(defaultWeekInYear);
      }
    }

    setAppliedFilterParams(initParams);
    loadDashboard(initParams, 'executive-overview');
    // Fetch last-refresh metadata for the first table group (tabs 1–4) on initial load.
    if (!weeklyAvgRefreshFetchedRef.current) {
      weeklyAvgRefreshFetchedRef.current = true;
      fetchOrderRulesLastRefresh('DBX_FACT_WEEKLY_AVG_QTY')
        .then((data) => setWeeklyAvgRefresh(data))
        .catch(() => { /* non-critical — header badge stays blank */ });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminLocationsFetchedAt, currentPeriodWeek]);

  // Fetch last-refresh for tab 5 (Max Potential) the first time the user lands there.
  useEffect(() => {
    if (activeTab !== 'max-potential') return;
    if (maxPotentialRefreshFetchedRef.current) return;
    maxPotentialRefreshFetchedRef.current = true;
    fetchOrderRulesLastRefresh('DBX_FACT_MAX_POTENTIAL')
      .then((data) => setMaxPotentialRefresh(data))
      .catch(() => { /* non-critical */ });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // Fetch last-refresh for tab 6 (Order Rule Check) the first time the user lands there.
  useEffect(() => {
    if (activeTab !== 'order-rule-check') return;
    if (orderRuleCheckRefreshFetchedRef.current) return;
    orderRuleCheckRefreshFetchedRef.current = true;
    fetchOrderRulesLastRefresh('DBX_FACT_ORDERRULECHECK')
      .then((data) => setOrderRuleCheckRefresh(data))
      .catch(() => { /* non-critical */ });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const handleApplyFilters = () => {
    setAppliedFilterParams(currentFilters);
    // Reset pagination to page 0 when filters change
    const resetReco = { page: 0, size: DEFAULT_PAGE_SIZE };
    const resetSales = { page: 0, size: DEFAULT_PAGE_SIZE };
    const resetCheck = { page: 0, size: DEFAULT_PAGE_SIZE };
    const resetMaxPot = { page: 0, size: DEFAULT_PAGE_SIZE };
    const resetSkuUtil = { page: 0, size: DEFAULT_PAGE_SIZE };
    setRecoPagination(resetReco);
    setSalesPagination(resetSales);
    setCheckPagination(resetCheck);
    setMaxPotPagination(resetMaxPot);
    setSkuUtilPagination(resetSkuUtil);
    loadDashboard(currentFilters, activeTab, { reco: resetReco, sales: resetSales, check: resetCheck, maxPot: resetMaxPot, skuUtil: resetSkuUtil });
    if (isMobileIos) {
      setMobileFilterOverlayVisible(false);
    }
  };

  const handleResetFilters = () => {
    const resetYear = { id: String(currentYear), displayText: String(currentYear) };

    setSelectedRegions([]);
    setSelectedMarkets([]);
    setSelectedLocations(initialLocationFromHeader);
    setSelectedCustomerBps([]);
    setSelectedCustomers([]);
    setSelectedKeyAccounts([]);
    setSelectedPackages([]);
    setSelectedMaterialNumbers([]);
    setSelectedMaterials([]);
    setSelectedYears([resetYear]);
    setSelectedPeriods(initialPeriodFromCurrentWeek);
    setSelectedWeeks(initialWeekFromCurrentWeek);
    setActiveQuickTimeFrame(null);

    const resetParams: OrderRulesFilterParams = { year: String(currentYear) };
    if (initialLocationFromHeader.length > 0) {
      resetParams.location = initialLocationFromHeader.map((location) => location.displayText).join(',');
    }
    if (initialPeriodFromCurrentWeek.length > 0) {
      resetParams.period = initialPeriodFromCurrentWeek.map((period) => String(period.id).replace(/^P/i, '')).join(',');
    }
    if (initialWeekFromCurrentWeek.length > 0) {
      resetParams.week = initialWeekFromCurrentWeek
        .map((week) => {
          const weekInYearNum = (week as any).weekInYearNum;
          return weekInYearNum != null ? String(weekInYearNum) : '';
        })
        .filter(Boolean)
        .join(',');
    }

    const resetReco = { page: 0, size: DEFAULT_PAGE_SIZE };
    const resetSales = { page: 0, size: DEFAULT_PAGE_SIZE };
    const resetCheck = { page: 0, size: DEFAULT_PAGE_SIZE };
    const resetMaxPot = { page: 0, size: DEFAULT_PAGE_SIZE };
    const resetSkuUtil = { page: 0, size: DEFAULT_PAGE_SIZE };
    setRecoPagination(resetReco);
    setSalesPagination(resetSales);
    setCheckPagination(resetCheck);
    setMaxPotPagination(resetMaxPot);
    setSkuUtilPagination(resetSkuUtil);

    setAppliedFilterParams(resetParams);
    loadDashboard(resetParams, activeTab, { reco: resetReco, sales: resetSales, check: resetCheck, maxPot: resetMaxPot, skuUtil: resetSkuUtil });
  };

  const handleRegionChange = (value: KPIFilterDropdownItem[]) => {
    setSelectedRegions(value);
    setSelectedMarkets([]);
    setSelectedLocations([]);
  };

  const handleMarketChange = (value: KPIFilterDropdownItem[]) => {
    setSelectedMarkets(value);
    setSelectedLocations([]);
  };

  const handleYearChange = (value: KPIFilterDropdownItem[]) => {
    setSelectedYears(value);
    setSelectedPeriods([]);
    setSelectedWeeks([]);
    setActiveQuickTimeFrame(null);
  };

  const handlePeriodChange = (value: KPIFilterDropdownItem[]) => {
    setSelectedPeriods(value);
    setSelectedWeeks([]);
    setActiveQuickTimeFrame(null);
  };

  const handleWeekChange = (value: KPIFilterDropdownItem[]) => {
    setSelectedWeeks(value);
    setActiveQuickTimeFrame(null);

    // Reverse-cascade: derive the set of periods implied by the selected weeks
    // and auto-select them so Period always reflects what Week shows.
    // Each week id is "P{period}W{weekInPeriod}" — extract the period number from it.
    if (value.length > 0) {
      const impliedPeriodNums = new Set<string>();
      value.forEach((week) => {
        const match = String(week.id).match(/^P(\d+)W/);
        if (match) impliedPeriodNums.add(`P${match[1]}`);
      });
      const newPeriods = periodOptions.filter((opt) => impliedPeriodNums.has(String(opt.id)));
      setSelectedPeriods(newPeriods);
    } else {
      // No weeks selected — clear periods too so the filter state is consistent.
      setSelectedPeriods([]);
    }
  };

  // Builds week dropdown items (with real WK_IN_FSCL_YR ids) for a given period,
  // restricted to the [fromWeek, toWeek] range (toWeek defaults to the period's last week).
  // Falls back to an assumed 4-week period if the period-weeks API data hasn't loaded yet.
  const buildWeekItemsForPeriod = useCallback(
    (year: number, period: number, fromWeek: number, toWeek?: number): KPIFilterDropdownItem[] => {
      const yearData = rawPeriodWeeksData.find((yd: any) => Number(yd.year) === year);
      const periodData = yearData ? (yearData.periods ?? []).find((pd: any) => Number(pd.period) === period) : null;
      const weeksRaw: any[] = periodData?.weeks ?? [];

      if (weeksRaw.length === 0) {
        const lastWeek = toWeek ?? 4;
        const items: KPIFilterDropdownItem[] = [];
        for (let weekInPeriod = fromWeek; weekInPeriod <= lastWeek; weekInPeriod++) {
          const id = `P${period}W${weekInPeriod}`;
          items.push({ id, displayText: id });
        }
        return items;
      }

      const lastWeek = toWeek ?? weeksRaw.length;
      const items: KPIFilterDropdownItem[] = [];
      weeksRaw.forEach((week: any, index: number) => {
        const weekInPeriod = index + 1;
        if (weekInPeriod < fromWeek || weekInPeriod > lastWeek) return;
        const weekInYear = typeof week === 'number' ? week : Number(week?.weekInYear ?? week?.week ?? weekInPeriod);
        items.push({
          id: `P${period}W${weekInPeriod}`,
          displayText: `W${weekInYear} (P${period}W${weekInPeriod})`,
          weekInYearNum: weekInYear,
        });
      });
      return items;
    },
    [rawPeriodWeeksData]
  );

  // Quick time-frame buttons set Year/Period/Week directly from today's period/week
  // (from the app's current period-week state) and apply immediately, without
  // requiring the user to press Apply.
  // Clicking the already-active button deselects it and clears Period/Week,
  // reverting to a year-only filter and re-applying immediately.
  const handleQuickTimeFrameSelect = useCallback(
    (key: QuickTimeFrameKey) => {
      // ── Toggle off: same button clicked again ─────────────────────────────
      if (activeQuickTimeFrame === key) {
        // Keep the year that was set by the quick-frame but clear period & week.
        setSelectedPeriods([]);
        setSelectedWeeks([]);
        setActiveQuickTimeFrame(null);

        const deselectedParams: OrderRulesFilterParams = {
          ...currentFilters,
          period: undefined,
          week: undefined,
        };
        setAppliedFilterParams(deselectedParams);
        loadDashboard(deselectedParams, activeTab);
        return;
      }

      // ── Toggle on: new or different button clicked ────────────────────────
      if (!currentPeriodWeek?.year || !currentPeriodWeek?.period || !currentPeriodWeek?.week) return;

      const todayYear = Number(currentPeriodWeek.year);
      const todayPeriod = Number(currentPeriodWeek.period);
      const todayWeekInPeriod = Number(currentPeriodWeek.week);

      let targetPeriod = todayPeriod;
      let targetWeekInPeriod = todayWeekInPeriod;
      let periodsToSelect: number[] = [todayPeriod];
      let weekItems: KPIFilterDropdownItem[] = buildWeekItemsForPeriod(todayYear, todayPeriod, todayWeekInPeriod, todayWeekInPeriod);

      if (key === 'wtd') {
        periodsToSelect = [todayPeriod];
        weekItems = buildWeekItemsForPeriod(todayYear, todayPeriod, todayWeekInPeriod, todayWeekInPeriod);
      } else if (key === 'ptd') {
        periodsToSelect = [todayPeriod];
        weekItems = buildWeekItemsForPeriod(todayYear, todayPeriod, 1, todayWeekInPeriod);
      } else if (key === 'qtd') {
        const quarterStartPeriod = Math.floor((todayPeriod - 1) / 3) * 3 + 1;
        periodsToSelect = [];
        weekItems = [];
        for (let period = quarterStartPeriod; period <= todayPeriod; period++) {
          periodsToSelect.push(period);
          const isCurrentPeriod = period === todayPeriod;
          weekItems.push(...buildWeekItemsForPeriod(todayYear, period, 1, isCurrentPeriod ? todayWeekInPeriod : undefined));
        }
      } else if (key === 'ytd') {
        periodsToSelect = [];
        weekItems = [];
        for (let period = 1; period <= todayPeriod; period++) {
          periodsToSelect.push(period);
          const isCurrentPeriod = period === todayPeriod;
          weekItems.push(...buildWeekItemsForPeriod(todayYear, period, 1, isCurrentPeriod ? todayWeekInPeriod : undefined));
        }
      } else if (key === 'lcw') {
        if (todayWeekInPeriod > 1) {
          targetPeriod = todayPeriod;
          targetWeekInPeriod = todayWeekInPeriod - 1;
        } else {
          targetPeriod = Math.max(1, todayPeriod - 1);
          targetWeekInPeriod = 4;
        }
        periodsToSelect = [targetPeriod];
        weekItems = buildWeekItemsForPeriod(todayYear, targetPeriod, targetWeekInPeriod, targetWeekInPeriod);
      } else if (key === 'lcp') {
        targetPeriod = Math.max(1, todayPeriod - 1);
        periodsToSelect = [targetPeriod];
        weekItems = buildWeekItemsForPeriod(todayYear, targetPeriod, 1);
      }

      const yearItem = yearOptions.find((year) => Number(year.id) === todayYear) ?? {
        id: String(todayYear),
        displayText: String(todayYear),
      };
      const periodItems = periodsToSelect.map(
        (period) => periodOptions.find((opt) => opt.id === `P${period}`) ?? { id: `P${period}`, displayText: `P${period}` }
      );

      setSelectedYears([yearItem]);
      setSelectedPeriods(periodItems);
      setSelectedWeeks(weekItems);
      setActiveQuickTimeFrame(key);

      const nextParams: OrderRulesFilterParams = {
        ...currentFilters,
        year: String(todayYear),
        period: periodItems.length ? periodItems.map((period) => String(period.id).replace(/^P/i, '')).join(',') : undefined,
        // Send the actual WK_IN_FSCL_YR (weekInYearNum), not the week-in-period ordinal.
        week: weekItems.length
          ? weekItems
              .map((week) => {
                const weekInYearNum = (week as any).weekInYearNum;
                return weekInYearNum != null ? String(weekInYearNum) : '';
              })
              .filter(Boolean)
              .join(',')
          : undefined,
      };

      setAppliedFilterParams(nextParams);
      loadDashboard(nextParams, activeTab);
    },
    [activeQuickTimeFrame, activeTab, currentPeriodWeek, yearOptions, periodOptions, buildWeekItemsForPeriod, currentFilters, loadDashboard]
  );

  // Clicking a dimension value in the Customer & SKU Utilization table adds/removes
  // it as a filter on the main filter bar and applies immediately, matching the
  // HTML report's "click any value to filter the report to it" behavior. Bumping
  // this tick after updating the relevant selection state lets `currentFilters`
  // (already reactive to that state) recompute before this effect re-applies it,
  // without duplicating the geography/param-precedence logic from currentFilters.
  const [quickApplyTick, setQuickApplyTick] = useState(0);

  useEffect(() => {
    if (quickApplyTick === 0) return;
    setAppliedFilterParams(currentFilters);
    loadDashboard(currentFilters, activeTab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quickApplyTick]);

  const handleToggleUtilizationFilter = useCallback(
    (dimension: QuickFilterDimension, value: string) => {
      const toggleIn = (
        list: KPIFilterDropdownItem[],
        options: KPIFilterDropdownItem[],
        setList: (next: KPIFilterDropdownItem[]) => void
      ) => {
        const isActive = list.some((item) => item.displayText === value);
        if (isActive) {
          setList(list.filter((item) => item.displayText !== value));
        } else {
          const match = options.find((opt) => opt.displayText === value) ?? { id: value, displayText: value };
          setList([...list, match]);
        }
      };

      switch (dimension) {
        case 'region': {
          const wasActive = selectedRegions.some((item) => item.displayText === value);
          toggleIn(selectedRegions, regionOptions, setSelectedRegions);
          if (!wasActive) {
            // Adding a new Region narrows the geography, so the now-stale
            // Market and Location selections are cleared, matching the
            // filter bar's own region -> market -> location cascade.
            setSelectedMarkets([]);
            setSelectedLocations([]);
          }
          break;
        }
        case 'market': {
          const wasActive = selectedMarkets.some((item) => item.displayText === value);
          toggleIn(selectedMarkets, marketOptions, setSelectedMarkets);
          if (!wasActive) {
            // Adding a new Market narrows the geography, so any existing
            // Location selection (which may belong to a different market)
            // is cleared.
            setSelectedLocations([]);
          }
          break;
        }
        case 'location':
          toggleIn(selectedLocations, locationOptions, setSelectedLocations);
          break;
        case 'customerBp':
          toggleIn(selectedCustomerBps, customerBpOptions, setSelectedCustomerBps);
          break;
        case 'customer':
          toggleIn(selectedCustomers, customerOptions, setSelectedCustomers);
          break;
        case 'keyAccount':
          toggleIn(selectedKeyAccounts, keyAccountOptions, setSelectedKeyAccounts);
          break;
        case 'packageName':
          toggleIn(selectedPackages, packageOptions, setSelectedPackages);
          break;
        case 'materialNumber':
          toggleIn(selectedMaterialNumbers, materialNumberOptions, setSelectedMaterialNumbers);
          break;
        case 'description':
          toggleIn(selectedMaterials, materialOptions, setSelectedMaterials);
          break;
        default:
          break;
      }

      setQuickApplyTick((tick) => tick + 1);
    },
    [
      selectedRegions,
      regionOptions,
      selectedMarkets,
      marketOptions,
      selectedLocations,
      locationOptions,
      selectedCustomerBps,
      customerBpOptions,
      selectedCustomers,
      customerOptions,
      selectedKeyAccounts,
      keyAccountOptions,
      selectedPackages,
      packageOptions,
      selectedMaterialNumbers,
      materialNumberOptions,
      selectedMaterials,
      materialOptions,
    ]
  );

  const utilizationActiveQuickFilters = useMemo<Record<QuickFilterDimension, Set<string>>>(
    () => ({
      region: new Set(selectedRegions.map((item) => item.displayText)),
      market: new Set(selectedMarkets.map((item) => item.displayText)),
      location: new Set(selectedLocations.map((item) => item.displayText)),
      customerBp: new Set(selectedCustomerBps.map((item) => item.displayText)),
      customer: new Set(selectedCustomers.map((item) => item.displayText)),
      keyAccount: new Set(selectedKeyAccounts.map((item) => item.displayText)),
      packageName: new Set(selectedPackages.map((item) => item.displayText)),
      materialNumber: new Set(selectedMaterialNumbers.map((item) => item.displayText)),
      description: new Set(selectedMaterials.map((item) => item.displayText)),
    }),
    [
      selectedRegions,
      selectedMarkets,
      selectedLocations,
      selectedCustomerBps,
      selectedCustomers,
      selectedKeyAccounts,
      selectedPackages,
      selectedMaterialNumbers,
      selectedMaterials,
    ]
  );

  const tabs = useMemo(
    () => [
      { key: 'executive-overview' as const, label: 'Executive Overview' },
      { key: 'recommendations' as const, label: 'Recommendations' },
      { key: 'sales-details' as const, label: 'Sales Details' },
      { key: 'customer-sku-utilization' as const, label: 'Customer & SKU Utilization' },
      { key: 'max-potential' as const, label: 'Max Potential Execution' },
      { key: 'order-rule-check' as const, label: 'Order Rule Check' },
    ],
    []
  );

  const disabledFilterKeySet = useMemo(
    () => new Set<ExtendedFilterKey>(TAB_DISABLED_FILTERS[activeTab] ?? []),
    [activeTab]
  );
  const disabledFilterTooltip = TAB_DISABLED_FILTER_TOOLTIP[activeTab];

  const {
    normalizedRecommendationRows,
    normalizedOpenRecommendationRows,
    normalizedSalesRows,
    normalizedCustomerRows,
    normalizedSkuRows,
    maxPotentialMetricCards,
    maxPotentialExecutionRows,
    normalizedCheckBuildRows,
  } = useOrderRulesDerivedData({
    orderRulesData,
    showSitesWithoutLayerLine,
    expandedMaxPotentialRows,
  });

  useEffect(() => {
    setExpandedMaxPotentialRows({});
  }, [showSitesWithoutLayerLine]);

  const toggleMaxPotentialRow = useCallback((key: string) => {
    setExpandedMaxPotentialRows((current) => ({ ...current, [key]: !current[key] }));
  }, []);

  // When the user switches tabs, reload the incoming tab's data if:
  //   a) it has never been fetched before (first activation), OR
  //   b) the global applied filters changed while the user was on another tab.
  // This covers both the initial per-tab load and the "stale on return" scenario
  // where filters were applied on a different tab.
  // Seeded with the initial tab (instead of null) so this effect doesn't also fire on
  // mount — the consolidated initial-load effect above already owns that first load.
  const prevTabRef = React.useRef<OrderRulesTab | null>(activeTab);
  useEffect(() => {
    if (prevTabRef.current === activeTab) return;
    prevTabRef.current = activeTab;

    const lastFetched = lastFetchedParamsByTab.current[activeTab];
    const currentParamsKey = JSON.stringify(appliedFilterParams);
    const filtersChangedSinceLastFetch = lastFetched !== currentParamsKey;

    if (lastFetched === undefined || filtersChangedSinceLastFetch) {
      loadDashboard(appliedFilterParams, activeTab);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // ── Pagination helpers ──────────────────────────────────────────────────────

  const handleRecoPageChange = useCallback(
    (newPage: number) => {
      const next = { ...recoPagination, page: newPage };
      setRecoPagination(next);
      loadDashboard(appliedFilterParams, 'recommendations', { reco: next });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [recoPagination, appliedFilterParams],
  );

  const handleSalesPageChange = useCallback(
    (newPage: number) => {
      const next = { ...salesPagination, page: newPage };
      setSalesPagination(next);
      loadDashboard(appliedFilterParams, 'sales-details', { sales: next });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [salesPagination, appliedFilterParams],
  );

  const handleCheckPageChange = useCallback(
    (newPage: number) => {
      const next = { ...checkPagination, page: newPage };
      setCheckPagination(next);
      loadDashboard(appliedFilterParams, 'order-rule-check', { check: next });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [checkPagination, appliedFilterParams],
  );

  const handleMaxPotPageChange = useCallback(
    (newPage: number) => {
      const next = { ...maxPotPagination, page: newPage };
      setMaxPotPagination(next);
      loadDashboard(appliedFilterParams, 'max-potential', { maxPot: next });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [maxPotPagination, appliedFilterParams],
  );

  const handleSkuUtilPageChange = useCallback(
    (newPage: number) => {
      const next = { ...skuUtilPagination, page: newPage };
      setSkuUtilPagination(next);
      loadDashboard(appliedFilterParams, 'customer-sku-utilization', { skuUtil: next });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [skuUtilPagination, appliedFilterParams],
  );

  // ── Page-size change helpers — resets to page 0, matching the One Pager UX ──
  const handleRecoPageSizeChange = useCallback(
    (size: number) => {
      const next = { ...recoPagination, page: 0, size };
      setRecoPagination(next);
      loadDashboard(appliedFilterParams, 'recommendations', { reco: next });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [recoPagination, appliedFilterParams],
  );

  const handleSalesPageSizeChange = useCallback(
    (size: number) => {
      const next = { ...salesPagination, page: 0, size };
      setSalesPagination(next);
      loadDashboard(appliedFilterParams, 'sales-details', { sales: next });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [salesPagination, appliedFilterParams],
  );

  const handleCheckPageSizeChange = useCallback(
    (size: number) => {
      const next = { ...checkPagination, page: 0, size };
      setCheckPagination(next);
      loadDashboard(appliedFilterParams, 'order-rule-check', { check: next });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [checkPagination, appliedFilterParams],
  );

  const handleMaxPotPageSizeChange = useCallback(
    (size: number) => {
      const next = { ...maxPotPagination, page: 0, size };
      setMaxPotPagination(next);
      loadDashboard(appliedFilterParams, 'max-potential', { maxPot: next });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [maxPotPagination, appliedFilterParams],
  );

  const handleSkuUtilPageSizeChange = useCallback(
    (size: number) => {
      const next = { ...skuUtilPagination, page: 0, size };
      setSkuUtilPagination(next);
      loadDashboard(appliedFilterParams, 'customer-sku-utilization', { skuUtil: next });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [skuUtilPagination, appliedFilterParams],
  );

  // ── Server-side sort helpers ────────────────────────────────────────────────
  // Cycles asc → desc → none (clears sortBy) and resets to page 0, then re-fetches
  // with sortBy (backend field name) + sortOrder in the request payload.
  const handleRecoSort = useCallback(
    (colKey: string) => {
      const dir: 'asc' | 'desc' | undefined =
        recoPagination.sortBy !== colKey ? 'asc' : recoPagination.sortDirection === 'asc' ? 'desc' : undefined;
      const next = { ...recoPagination, page: 0, sortBy: dir ? colKey : undefined, sortDirection: dir };
      setRecoPagination(next);
      loadDashboard(appliedFilterParams, 'recommendations', { reco: next });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [recoPagination, appliedFilterParams],
  );

  const handleSalesSort = useCallback(
    (colKey: string) => {
      const dir: 'asc' | 'desc' | undefined =
        salesPagination.sortBy !== colKey ? 'asc' : salesPagination.sortDirection === 'asc' ? 'desc' : undefined;
      const next = { ...salesPagination, page: 0, sortBy: dir ? colKey : undefined, sortDirection: dir };
      setSalesPagination(next);
      loadDashboard(appliedFilterParams, 'sales-details', { sales: next });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [salesPagination, appliedFilterParams],
  );

  const handleCheckSort = useCallback(
    (colKey: string) => {
      const dir: 'asc' | 'desc' | undefined =
        checkPagination.sortBy !== colKey ? 'asc' : checkPagination.sortDirection === 'asc' ? 'desc' : undefined;
      const next = { ...checkPagination, page: 0, sortBy: dir ? colKey : undefined, sortDirection: dir };
      setCheckPagination(next);
      loadDashboard(appliedFilterParams, 'order-rule-check', { check: next });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [checkPagination, appliedFilterParams],
  );

  const handleShowNonEligibleSkusChange = useCallback(
    (value: boolean) => {
      setShowNonEligibleSkus(value);
      const next = { ...checkPagination, page: 0 };
      setCheckPagination(next);
      loadDashboard(
        appliedFilterParams,
        'order-rule-check',
        { check: next },
        { showNonEligibleSkus: value, showOnlyReviewed, showOnlyNonInvoicedSkus },
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [checkPagination, appliedFilterParams, showOnlyReviewed, showOnlyNonInvoicedSkus],
  );

  const handleShowOnlyNonInvoicedSkusChange = useCallback(
    (value: boolean) => {
      setShowOnlyNonInvoicedSkus(value);
      const next = { ...checkPagination, page: 0 };
      setCheckPagination(next);
      loadDashboard(
        appliedFilterParams,
        'order-rule-check',
        { check: next },
        { showNonEligibleSkus, showOnlyReviewed, showOnlyNonInvoicedSkus: value },
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [checkPagination, appliedFilterParams, showNonEligibleSkus, showOnlyReviewed],
  );

  const handleShowOnlyReviewedChange = useCallback(
    (value: boolean) => {
      setShowOnlyReviewed(value);
      const next = { ...checkPagination, page: 0 };
      setCheckPagination(next);
      loadDashboard(
        appliedFilterParams,
        'order-rule-check',
        { check: next },
        { showNonEligibleSkus, showOnlyReviewed: value, showOnlyNonInvoicedSkus },
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [checkPagination, appliedFilterParams, showNonEligibleSkus, showOnlyNonInvoicedSkus],
  );

  const handleOrderRuleCheckReviewToggleSuccess = useCallback(async () => {
    // Force a reload even when request params are unchanged.
    lastLoadRequestRef.current = null;
    await loadDashboard(
      appliedFilterParams,
      'order-rule-check',
      { check: checkPagination },
      { showNonEligibleSkus, showOnlyReviewed, showOnlyNonInvoicedSkus },
    );
  }, [
    appliedFilterParams,
    checkPagination,
    loadDashboard,
    showNonEligibleSkus,
    showOnlyReviewed,
    showOnlyNonInvoicedSkus,
  ]);

  const handleMaxPotSort = useCallback(
    (colKey: string) => {
      const dir: 'asc' | 'desc' | undefined =
        maxPotPagination.sortBy !== colKey ? 'asc' : maxPotPagination.sortDirection === 'asc' ? 'desc' : undefined;
      const next = { ...maxPotPagination, page: 0, sortBy: dir ? colKey : undefined, sortDirection: dir };
      setMaxPotPagination(next);
      loadDashboard(appliedFilterParams, 'max-potential', { maxPot: next });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [maxPotPagination, appliedFilterParams],
  );

  const handleSkuUtilSort = useCallback(
    (colKey: string) => {
      const dir: 'asc' | 'desc' | undefined =
        skuUtilPagination.sortBy !== colKey ? 'asc' : skuUtilPagination.sortDirection === 'asc' ? 'desc' : undefined;
      const next = { ...skuUtilPagination, page: 0, sortBy: dir ? colKey : undefined, sortDirection: dir };
      setSkuUtilPagination(next);
      loadDashboard(appliedFilterParams, 'customer-sku-utilization', { skuUtil: next });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [skuUtilPagination, appliedFilterParams],
  );

  // Fires when a column checkbox is toggled in the Customer & SKU Utilization table's
  // Columns dropdown — re-fetches with the mapped `granularity` values (or clears them).
  const handleSkuUtilGranularityChange = useCallback(
    (granularities: string[]) => {
      setSkuUtilGranularity(granularities);
      const next = { ...skuUtilPagination, page: 0 };
      setSkuUtilPagination(next);
      loadDashboard(appliedFilterParams, 'customer-sku-utilization', { skuUtil: next }, undefined, granularities);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [skuUtilPagination, appliedFilterParams],
  );

  /**
   * Export the active tab's table via POST /order-rules/export, reusing the same applied
   * filters / sort / toggles / granularity that the tab's data API was last called with.
   */
  const handleExport = useCallback(
    async (reportType: OrderRulesExportReportType) => {
      const mapSort = (
        p: { page: number; size: number; sortBy?: string; sortDirection?: 'asc' | 'desc' },
        fieldMap: Record<string, string>,
      ) => ({ ...p, sortBy: p.sortBy ? fieldMap[p.sortBy] ?? p.sortBy : undefined });

      const options: OrderRulesExportOptions = {};
      switch (reportType) {
        case 'recommendations':
          options.pagination = mapSort(recoPagination, RECO_SORT_FIELD_MAP);
          break;
        case 'avg-weekly-sales':
          options.pagination = mapSort(salesPagination, SALES_SORT_FIELD_MAP);
          break;
        case 'customer-sku-utilization':
          options.pagination = mapSort(skuUtilPagination, SKU_UTIL_SORT_FIELD_MAP);
          options.granularity = skuUtilGranularity;
          break;
        case 'max-potential-execution':
          options.pagination = mapSort(maxPotPagination, MAX_POT_SORT_FIELD_MAP);
          break;
        case 'orderrule-check':
          options.pagination = checkPagination;
          options.showNonEligibleSkus = showNonEligibleSkus;
          options.showReviewed = showOnlyReviewed;
          options.showNonInvoicedSkus = showOnlyNonInvoicedSkus;
          break;
      }

      showToast({ header: 'Export Started', subtext: 'Your export is being prepared.', variant: 'info', position: 'top' });
      try {
        await exportOrderRulesReport(reportType, appliedFilterParams, getLocationIds(selectedLocations), options);
        showToast({ header: 'Export Successful', subtext: 'Your export has been downloaded.', variant: 'success', position: 'top' });
      } catch (error: any) {
        const message = error?.response?.data?.message || error?.message || 'An unexpected error occurred while exporting.';
        showToast({ header: 'Export Failed', subtext: message, variant: 'error', position: 'top' });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [appliedFilterParams, selectedLocations, getLocationIds, recoPagination, salesPagination, skuUtilPagination, maxPotPagination, checkPagination, skuUtilGranularity, showNonEligibleSkus, showOnlyReviewed, showOnlyNonInvoicedSkus],
  );

  // ── Pagination bar — mirrors the KPI One Pager's "Showing X–Y of Z" +
  // Prev/Next + page-size selector layout so every paginated tab is consistent.
  const renderPagination = (
    currentPage: number,
    totalPages: number,
    totalElements: number,
    pageSize: number,
    onPageChange: (page: number) => void,
    onPageSizeChange?: (size: number) => void,
  ) => {
    if (totalElements === 0) return null;
    const startRow = totalElements === 0 ? 0 : currentPage * pageSize + 1;
    const endRow = Math.min((currentPage + 1) * pageSize, totalElements);
    return (
      <View style={styles.paginationRow}>
        <Text style={styles.paginationLabel}>
          {`Showing ${startRow}–${endRow} of ${totalElements.toLocaleString('en-US')}`}
        </Text>
        <View style={styles.paginationBtnGroup}>
          <TouchableOpacity
            style={[styles.paginationBtn, currentPage === 0 && styles.paginationBtnDisabled]}
            disabled={currentPage === 0}
            onPress={() => onPageChange(currentPage - 1)}
            accessibilityLabel="Previous page"
          >
            <MaterialIcons name="chevron-left" size={18} color={currentPage === 0 ? '#9CA3AF' : '#FFFFFF'} />
          </TouchableOpacity>
          <Text style={styles.paginationPageText}>{`Page ${currentPage + 1} of ${Math.max(totalPages, 1)}`}</Text>
          <TouchableOpacity
            style={[styles.paginationBtn, currentPage >= totalPages - 1 && styles.paginationBtnDisabled]}
            disabled={currentPage >= totalPages - 1}
            onPress={() => onPageChange(currentPage + 1)}
            accessibilityLabel="Next page"
          >
            <MaterialIcons name="chevron-right" size={18} color={currentPage >= totalPages - 1 ? '#9CA3AF' : '#FFFFFF'} />
          </TouchableOpacity>
        </View>
        {onPageSizeChange && (
          <View style={styles.paginationSizeSelector}>
            {[25, 50, 100].map((size) => (
              <TouchableOpacity
                key={size}
                style={[styles.paginationSizeOption, pageSize === size && styles.paginationSizeOptionActive]}
                onPress={() => onPageSizeChange(size)}
                activeOpacity={0.7}
              >
                <Text style={[styles.paginationSizeText, pageSize === size && styles.paginationSizeTextActive]}>
                  {size}
                </Text>
              </TouchableOpacity>
            ))}
          </View>
        )}
      </View>
    );
  };

  const renderActiveTab = () => {
    if (activeTab === 'executive-overview') {
      return (
        <ExecutiveOverviewTab
          loading={summaryLoading}
          summaryLoading={summaryLoading}
          maxPotentialLoading={maxPotentialChartLoading}
          enrollmentTrendLoading={enrollmentTrendLoading}
          layerPerformanceLoading={layerPerformanceLoading}
          kpis={orderRulesData?.kpis ?? []}
          summaryDrillLevel={orderRulesData?.summaryDrillLevel}
          maxPotentialDrillLevel={orderRulesData?.maxPotentialDrillLevel}
          skuRecommendationSummary={orderRulesData?.skuRecommendationSummary ?? []}
          maxPotentialExecution={orderRulesData?.maxPotentialExecution ?? []}
          enrollmentTrend={orderRulesData?.enrollmentTrend ?? []}
          enrollmentTimeGrain={orderRulesData?.enrollmentTimeGrain}
          enrollmentXAxisLabel={orderRulesData?.enrollmentXAxisLabel}
          enrollmentYAxisLabel={orderRulesData?.enrollmentYAxisLabel}
          layerPerformanceTrend={orderRulesData?.layerPerformanceTrend ?? []}
          enrollmentInsufficientDataMessage={orderRulesData?.enrollmentInsufficientDataMessage}
          layerPerformanceInsufficientDataMessage={orderRulesData?.layerPerformanceInsufficientDataMessage}
          layerPerformanceTimeGrain={orderRulesData?.layerPerformanceTimeGrain}
          layerPerformanceXAxisLabel={orderRulesData?.layerPerformanceXAxisLabel}
          layerPerformanceYAxisLabel={orderRulesData?.layerPerformanceYAxisLabel}
          maxPotentialViewMode={execMaxPotentialViewMode}
          onMaxPotentialViewModeChange={setExecMaxPotentialViewMode}
        />
      );
    }

    if (activeTab === 'recommendations') {
      return (
        <View style={styles.sectionGap}>
          <RecommendationsTab
            loading={recommendationsLoading}
            recommendationRows={normalizedRecommendationRows}
            openRecommendationRows={normalizedOpenRecommendationRows}
            recommendationWindowStart={orderRulesData?.recommendationWindowStart}
            recommendationWindowEnd={orderRulesData?.recommendationWindowEnd}
            sort={{ col: recoPagination.sortBy ?? null, dir: recoPagination.sortDirection ?? null }}
            onSort={handleRecoSort}
            pagination={renderPagination(recoPagination.page, recoTotalPages, recoTotalElements, recoPagination.size, handleRecoPageChange, handleRecoPageSizeChange)}
            onExport={() => handleExport('recommendations')}
          />
        </View>
      );
    }

    if (activeTab === 'sales-details') {
      return (
        <View style={styles.sectionGap}>
          <View style={styles.card}>
            <View style={styles.cardHeaderRow}>
              <View>
                <Text style={styles.cardTitle}>Average Weekly Sales Volume</Text>
                <Text style={styles.cardSubTitle}>Order history by SKU and customer, scoped by the Year / Period / Week filters</Text>
              </View>
              <ExportButton testID="order-rules-export-sales-details" onExport={() => handleExport('avg-weekly-sales')} />
            </View>
            <SalesDetailsTab
              loading={salesDetailsLoading}
              rows={normalizedSalesRows}
              sort={{ col: salesPagination.sortBy ?? null, dir: salesPagination.sortDirection ?? null }}
              onSort={handleSalesSort}
              pagination={renderPagination(salesPagination.page, salesTotalPages, salesTotalElements, salesPagination.size, handleSalesPageChange, handleSalesPageSizeChange)}
            />
          </View>
        </View>
      );
    }

    if (activeTab === 'customer-sku-utilization') {
      return (
        <View style={styles.sectionGap}>
          <CustomerSkuUtilizationTab
            loading={customerSkuLoading}
            customerRows={normalizedCustomerRows}
            skuRows={normalizedSkuRows}
            activeQuickFilters={utilizationActiveQuickFilters}
            onToggleQuickFilter={handleToggleUtilizationFilter}
            sort={{ col: skuUtilPagination.sortBy ?? null, dir: skuUtilPagination.sortDirection ?? null }}
            onSort={handleSkuUtilSort}
            onGranularityChange={handleSkuUtilGranularityChange}
            pagination={renderPagination(skuUtilPagination.page, skuUtilTotalPages, skuUtilTotalElements, skuUtilPagination.size, handleSkuUtilPageChange, handleSkuUtilPageSizeChange)}
            totalElements={skuUtilTotalElements}
            onExport={() => handleExport('customer-sku-utilization')}
          />
        </View>
      );
    }

    if (activeTab === 'max-potential') {
      return (
        <View style={styles.sectionGap}>
          <MaxPotentialTab
            loading={maxPotentialTableLoading}
            metricCards={maxPotentialMetricCards}
            showSitesWithoutLayerLine={showSitesWithoutLayerLine}
            onToggleSitesWithoutLayerLine={() => setShowSitesWithoutLayerLine((current) => !current)}
            rows={maxPotentialExecutionRows}
            onToggleRow={toggleMaxPotentialRow}
            sort={{ col: maxPotPagination.sortBy ?? null, dir: maxPotPagination.sortDirection ?? null }}
            onSort={handleMaxPotSort}
            pagination={renderPagination(maxPotPagination.page, maxPotTotalPages, maxPotTotalElements, maxPotPagination.size, handleMaxPotPageChange, handleMaxPotPageSizeChange)}
            onExport={() => handleExport('max-potential-execution')}
          />
        </View>
      );
    }

    return (
      <View style={styles.sectionGap}>
        <OrderRuleCheckTab
          loading={orderRuleCheckLoading}
          checkBuildRows={normalizedCheckBuildRows}
          sort={{ col: checkPagination.sortBy ?? null, dir: checkPagination.sortDirection ?? null }}
          onSort={handleCheckSort}
          showNonEligibleSkus={showNonEligibleSkus}
          onShowNonEligibleSkusChange={handleShowNonEligibleSkusChange}
          showOnlyReviewed={showOnlyReviewed}
          onShowOnlyReviewedChange={handleShowOnlyReviewedChange}
          showOnlyNonInvoicedSkus={showOnlyNonInvoicedSkus}
          onShowOnlyNonInvoicedSkusChange={handleShowOnlyNonInvoicedSkusChange}
          onReviewToggleSuccess={handleOrderRuleCheckReviewToggleSuccess}
          pagination={renderPagination(checkPagination.page, checkTotalPages, checkTotalElements, checkPagination.size, handleCheckPageChange, handleCheckPageSizeChange)}
          onExport={() => handleExport('orderrule-check')}
        />
      </View>
    );
  };

  if (isMobileIos && isMobileFilterOverlayVisible) {
    return (
      <View style={styles.mobileOverlayContainer}>
        <ScrollView style={styles.mobileOverlayBody} contentContainerStyle={styles.mobileOverlayBodyContent}>
          <OrderRulesFilterBar
            openFilterKey={openFilterKey}
            setOpenFilterKey={setOpenFilterKey}
            regionOptions={regionOptions}
            selectedRegions={selectedRegions}
            onRegionChange={handleRegionChange}
            marketOptions={marketOptions}
            selectedMarkets={selectedMarkets}
            onMarketChange={handleMarketChange}
            locationOptions={locationOptions}
            selectedLocations={selectedLocations}
            onLocationChange={setSelectedLocations}
            customerBpOptions={customerBpOptions}
            selectedCustomerBps={selectedCustomerBps}
            onCustomerBpChange={setSelectedCustomerBps}
            onCustomerBpSearchChange={handleCustomerBpSearch}
            customerOptions={customerOptions}
            selectedCustomers={selectedCustomers}
            onCustomerChange={setSelectedCustomers}
            onCustomerSearchChange={handleCustomerSearch}
            keyAccountOptions={keyAccountOptions}
            selectedKeyAccounts={selectedKeyAccounts}
            onKeyAccountChange={setSelectedKeyAccounts}
            onKeyAccountSearchChange={handleKeyAccountSearch}
            packageOptions={packageOptions}
            selectedPackages={selectedPackages}
            onPackageChange={setSelectedPackages}
            onPackageSearchChange={handlePackageSearch}
            materialNumberOptions={materialNumberOptions}
            selectedMaterialNumbers={selectedMaterialNumbers}
            onMaterialNumberChange={setSelectedMaterialNumbers}
            onMaterialNumberSearchChange={handleMaterialNumberSearch}
            materialOptions={materialOptions}
            selectedMaterials={selectedMaterials}
            onMaterialChange={setSelectedMaterials}
            onMaterialSearchChange={handleMaterialSearch}
            yearOptions={yearOptions}
            selectedYears={selectedYears}
            onYearChange={handleYearChange}
            periodOptions={periodOptions}
            selectedPeriods={selectedPeriods}
            onPeriodChange={handlePeriodChange}
            disabledPeriodIds={disabledPeriodIds}
            weekOptions={weekOptions}
            selectedWeeks={selectedWeeks}
            onWeekChange={handleWeekChange}
            disabledWeekIds={disabledWeekIds}
            onReset={handleResetFilters}
            onApply={handleApplyFilters}
            isApplyEnabled={isApplyEnabled}
            disabledFilterKeys={disabledFilterKeySet}
            disabledFilterTooltip={disabledFilterTooltip}
            activeQuickTimeFrame={activeQuickTimeFrame}
            onQuickTimeFrameSelect={handleQuickTimeFrameSelect}
          />
        </ScrollView>
        <View style={styles.mobileOverlayFooter}>
          <TouchableOpacity
            style={styles.mobileOverlayCancelBtn}
            onPress={() => setMobileFilterOverlayVisible(false)}
            testID="order-rules-mobile-cancel"
          >
            <Text style={styles.mobileOverlayCancelBtnText}>Close</Text>
          </TouchableOpacity>

          <TouchableOpacity
            style={styles.mobileOverlayResetBtn}
            onPress={handleResetFilters}
            testID="order-rules-mobile-reset"
          >
            <Text style={styles.mobileOverlayResetBtnText}>Reset</Text>
          </TouchableOpacity>

          <TouchableOpacity
            style={styles.mobileOverlayApplyBtn}
            onPress={handleApplyFilters}
            disabled={!isApplyEnabled}
            testID="order-rules-mobile-apply"
          >
            <Text style={styles.mobileOverlayApplyBtnText}>Apply</Text>
          </TouchableOpacity>
        </View>
      </View>
    );
  }

  return (
    <ScrollView style={styles.container} contentContainerStyle={styles.contentContainer} showsVerticalScrollIndicator>
      <View style={styles.breadcrumbWrap}>
        <DLOSimpleBreadcrumb
          path={['Analytics Hub', 'Reports', title]}
          onHomePress={() => {
            try {
              const globalNavigator = (globalThis as any).appNavigate;
              if (typeof globalNavigator === 'function') {
                globalNavigator('Home');
              }
            } catch {
              // No-op fallback: breadcrumb remains visual only when global navigation is unavailable.
            }
          }}
          onNavigate={(index) => {
            try {
              const globalNavigator = (globalThis as any).appNavigate;
              if (typeof globalNavigator === 'function') {
                if (index === 0) {
                  // "Analytics Hub" → open Analytics Hub on default tab
                  globalNavigator('Analytics');
                } else if (index === 1) {
                  // "Reports" → open Analytics Hub with Reports tab active
                  globalNavigator('Analytics', { initialTab: 'all-reports' });
                }
              }
            } catch {
              // No-op fallback.
            }
          }}
        />
      </View>

      <View style={styles.header}>
        <View style={styles.headerLeft}>
          <View style={styles.headerIconWrap}>
            <MaterialIcons name={iconName} size={20} color="#005CBC" />
          </View>
          <View>
            <Text style={styles.title}>{title}</Text>
            <Text style={styles.subtitle}>{subtitle}</Text>
          </View>
        </View>
        <View style={styles.headerMetaRow}>
          <View style={styles.refreshedBadge}>
            <Text style={styles.refreshedBadgeText}>Refreshed: </Text>
            <Text style={styles.refreshedBadgeValueText}>{lastRefreshedLabel || '--'}</Text>
          </View>
          <Text style={styles.headerMetaSeparator}>•</Text>
          <View style={styles.refreshedBadge}>
            <Text style={styles.refreshedBadgeText}>Recommendation window: </Text>
            <Text style={styles.refreshedBadgeValueText}>{recommendationWindowLabel}</Text>
          </View>
        </View>
      </View>

      <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.tabBar} contentContainerStyle={styles.tabBarContent}>
        {tabs.map((tab) => {
          const isActive = activeTab === tab.key;
          return (
            <TouchableOpacity key={tab.key} style={[styles.tab, isActive && styles.tabActive]} onPress={() => setActiveTab(tab.key)}>
              <Text style={[styles.tabLabel, isActive && styles.tabLabelActive]}>{tab.label}</Text>
            </TouchableOpacity>
          );
        })}
      </ScrollView>

      {isMobileIos ? (
        <View style={styles.mobileFilterTriggerRow}>
          <TouchableOpacity
            style={styles.mobileFilterButton}
            onPress={() => setMobileFilterOverlayVisible(true)}
            testID="order-rules-mobile-filter-open"
          >
            <MaterialIcons name="filter-list" size={16} color="#FFFFFF" />
            <Text style={styles.mobileFilterButtonText}>Filters</Text>
          </TouchableOpacity>
        </View>
      ) : null}

      {!isMobileIos ? (
        <View style={styles.filterBarWrapper}>
          <OrderRulesFilterBar
            openFilterKey={openFilterKey}
            setOpenFilterKey={setOpenFilterKey}
            regionOptions={regionOptions}
            selectedRegions={selectedRegions}
            onRegionChange={handleRegionChange}
            marketOptions={marketOptions}
            selectedMarkets={selectedMarkets}
            onMarketChange={handleMarketChange}
            locationOptions={locationOptions}
            selectedLocations={selectedLocations}
            onLocationChange={setSelectedLocations}
            customerBpOptions={customerBpOptions}
            selectedCustomerBps={selectedCustomerBps}
            onCustomerBpChange={setSelectedCustomerBps}
            onCustomerBpSearchChange={handleCustomerBpSearch}
            customerOptions={customerOptions}
            selectedCustomers={selectedCustomers}
            onCustomerChange={setSelectedCustomers}
            onCustomerSearchChange={handleCustomerSearch}
            keyAccountOptions={keyAccountOptions}
            selectedKeyAccounts={selectedKeyAccounts}
            onKeyAccountChange={setSelectedKeyAccounts}
            onKeyAccountSearchChange={handleKeyAccountSearch}
            packageOptions={packageOptions}
            selectedPackages={selectedPackages}
            onPackageChange={setSelectedPackages}
            onPackageSearchChange={handlePackageSearch}
            materialNumberOptions={materialNumberOptions}
            selectedMaterialNumbers={selectedMaterialNumbers}
            onMaterialNumberChange={setSelectedMaterialNumbers}
            onMaterialNumberSearchChange={handleMaterialNumberSearch}
            materialOptions={materialOptions}
            selectedMaterials={selectedMaterials}
            onMaterialChange={setSelectedMaterials}
            onMaterialSearchChange={handleMaterialSearch}
            yearOptions={yearOptions}
            selectedYears={selectedYears}
            onYearChange={handleYearChange}
            periodOptions={periodOptions}
            selectedPeriods={selectedPeriods}
            onPeriodChange={handlePeriodChange}
            disabledPeriodIds={disabledPeriodIds}
            weekOptions={weekOptions}
            selectedWeeks={selectedWeeks}
            onWeekChange={handleWeekChange}
            disabledWeekIds={disabledWeekIds}
            onReset={handleResetFilters}
            onApply={handleApplyFilters}
            isApplyEnabled={isApplyEnabled}
            disabledFilterKeys={disabledFilterKeySet}
            disabledFilterTooltip={disabledFilterTooltip}
            activeQuickTimeFrame={activeQuickTimeFrame}
            onQuickTimeFrameSelect={handleQuickTimeFrameSelect}
          />
        </View>
      ) : null}

      <View style={styles.tabContentContainer}>{renderActiveTab()}</View>

      <DLOModal
        visible={apiErrorModal.visible}
        onClose={dismissApiError}
        title={apiErrorModal.title}
        primaryButtonText="OK"
        secondaryButtonText=""
        onPrimaryPress={dismissApiError}
        hideButtons={false}
        showCloseIcon
      >
        <Text style={styles.errorModalMessage}>{apiErrorModal.message}</Text>
      </DLOModal>
    </ScrollView>
  );
};

export default OrderRulesReport;
